import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Trash2, ChevronDown, Package, PackageOpen, Download, MapPin, Phone, MessageCircle, Copy, Truck, ExternalLink, Printer, RefreshCw, AlertTriangle, ArrowLeft, Store, Users, Building2, Undo2, ListChecks, Camera, Plus, Search, X, Clock, Edit2, Check } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import PhoneActions from '../components/PhoneActions';
import OrderChat from '../components/OrderChat';
import AdminOrderSearch from '../components/AdminOrderSearch';
import ReceivedDocumentsPanel from '../components/ReceivedDocumentsPanel';
import ManualAllocateModal from '../components/ManualAllocateModal';
import ShortfallFillModal from '../components/ShortfallFillModal';
import ErrorBoundary from '../components/ErrorBoundary';
import LoadingPicturesModal from '../components/LoadingPicturesModal';
import NewOrderModal from '../components/NewOrderModal';
import NewCustomerOrderModal from '../components/NewCustomerOrderModal';
import NewExportOrderModal from '../components/NewExportOrderModal';
import LoadingPicturesSection from '../components/LoadingPicturesSection';
import PostPickupProofSection from '../components/PostPickupProofSection';
import { getStatusAccent } from '../lib/orderStatus';
import { orderPartyPrimary, orderPartyContact } from '../lib/orderParty';
import SuperAdminOrderOverride from '../components/SuperAdminOrderOverride';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import InlineOrderItemsEditor from '../components/InlineOrderItemsEditor';
import WmsTaskAssignPanel from '../components/WmsTaskAssignPanel';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { useAdminPermissions } from '../hooks/useAdminPermissions';
import { formatDateTime, formatDate } from '../lib/dateFormat';
import Rial from '../components/Rial';

const STATUSES = ['draft', 'new', 'confirmed', 'confirmed_by_recipient', 'assigned', 'picked_up', 'out_for_delivery', 'delivered', 'received', 'failed', 'cancelled'];
// Feb 2026 — status pill palette (per ops brief):
//   confirmed        → blue     PENDING/new/draft → grey
//   assigned         → yellow (PROCESSING)
//   picked_up        → orange
//   out_for_delivery → purple (DISPATCHED)
//   delivered/received → green
//   cancelled/failed → red
const statusColors = {
  draft: 'bg-neutral-200 text-neutral-700',
  new: 'bg-neutral-200 text-neutral-700',
  confirmed: 'bg-blue-100 text-blue-700',
  assigned: 'bg-yellow-100 text-yellow-800',
  picked_up: 'bg-orange-100 text-orange-700',
  out_for_delivery: 'bg-purple-100 text-purple-700',
  delivered: 'bg-emerald-100 text-emerald-700',
  received: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-red-100 text-red-700',
};

// Feb 2026 — click-to-copy system-order-ID pill. Super-admin only.
// Needed when the data-tools endpoints (e.g.
// `restore-order-reservations/{order_id}`) take the UUID not the
// human order_number, and ops have to look it up repeatedly.
const SystemOrderIdPill = ({ orderId, testIdSuffix }) => {
  const [copied, setCopied] = React.useState(false);
  if (!orderId) return null;
  const onCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(orderId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (_err) { /* clipboard blocked — the raw ID is already visible */ }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      title={copied ? 'Copied!' : `Click to copy system ID\n${orderId}`}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[10px] bg-neutral-100 text-neutral-600 hover:bg-neutral-200 hover:text-[#0F172A] border border-neutral-200 transition"
      data-testid={`order-system-id-${testIdSuffix || orderId}`}
    >
      <span className="opacity-60">ID</span>
      <span>{orderId.slice(0, 8)}…{orderId.slice(-4)}</span>
      {copied ? (
        <Check className="w-3 h-3 text-emerald-600" />
      ) : (
        <Copy className="w-3 h-3 opacity-70" />
      )}
    </button>
  );
};


/**
 * Feb 2026 — compact activity-by chip row rendered under the order
 * category list. Each chip shows the ACTION label in muted text and
 * one or more SHORT first names (comma-separated) in a readable
 * weight. Renders nothing when no action has been recorded, so the
 * card stays uncluttered on fresh orders.
 */
const OrderActivityByChips = ({ activity, orderId }) => {
  if (!activity) return null;
  const rows = [
    { label: 'Created by', value: activity.created ? [activity.created] : [], key: 'created' },
    { label: 'Picked by', value: activity.pickers || [], key: 'picked' },
    { label: 'Loaded by', value: activity.loaded || [], key: 'loaded' },
    { label: 'Delivered by', value: activity.delivered || [], key: 'delivered' },
  ].filter((r) => Array.isArray(r.value) && r.value.length > 0);
  if (rows.length === 0) return null;
  return (
    <div
      className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px]"
      data-testid={`order-activity-by-${orderId}`}
    >
      {rows.map((r) => (
        <span
          key={r.key}
          className="inline-flex items-baseline gap-1"
          data-testid={`order-activity-${r.key}-${orderId}`}
        >
          <span className="text-neutral-400">{r.label}</span>
          <span className="font-medium text-neutral-700">
            {r.value.join(', ')}
          </span>
        </span>
      ))}
    </div>
  );
};




const AdminOrders = () => {
  const { authHeaders } = useAdminAuth();
  const { toast } = useToast();
  const { can } = useAdminPermissions();

  // Field-visibility flags — Super admin always sees everything; sub-admins
  // get whatever the super-admin checked. Always-visible fields (customer
  // name, status, timestamp) are intentionally not gated.
  const showTotal           = can('orders.field_total');
  const showItemCount       = can('orders.field_item_count');
  const showPhoneActions    = can('orders.field_phone_actions');
  const showAddress         = can('orders.field_address');
  const showNotes           = can('orders.field_notes');
  const showLocationMap     = can('orders.field_location_map');
  const showCustomerEmail   = can('orders.field_customer_email');
  const showItemsTable      = can('orders.field_items_table');
  const showItemsAmount     = can('orders.field_items_amount');
  const showPricingBreakdown= can('orders.field_pricing_breakdown');
  const showDriverPanel     = can('orders.field_driver_panel');
  const showChat            = can('orders.field_chat');
  const showInvoiceDownload = can('orders.field_invoice_download');
  const showPublicTracker   = can('orders.field_public_tracker');
  // Action gates
  const canEditStatus       = can('orders.edit_status');
  const canAssignDriver     = can('orders.assign_driver');
  const canAssignWmsTasks   = can('orders.assign_wms_tasks');
  const canReassignPicker   = can('warehouse.reassign_picker_inline');
  const canDelete           = can('orders.delete');
  const [orders, setOrders] = useState([]);
  // Feb 2026 — Per-order picking flag ("fully" | "partial"). Fetched
  // once on load; drives the Fully/Partial Picked chip on each row.
  const [pickingFlags, setPickingFlags] = useState({});
  // Feb 2026 — Simple info dialog reused across the page for
  // workflow-gate messages (DN not available yet, pallet quantities
  // required, etc). `{title, message} | null`.
  const [gateDialog, setGateDialog] = useState(null);
  // Feb 2026 — Short-pallet shipping guard. When the backend returns
  // 409 `ship_short_pallet` on a delivered/shipped transition we open
  // this dialog listing the shortfall. Super admins get an "Approve
  // short ship" button that retries the status update with
  // `allow_short_pallet_ship: true`; everyone else sees the fix-count
  // guidance only.
  const [shortPalletDialog, setShortPalletDialog] = useState(null);
  const [shortPalletSubmitting, setShortPalletSubmitting] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [branches, setBranches] = useState([]);
  // Feb-2026 v5: tiled landing view.  `view` controls the top-level
  // screen — 'landing' (two big tiles), 'branch' (per-branch tiles or,
  // once `selectedBranchId` is set, the filtered order list), or
  // 'customer' (the existing customer-order list).
  const [view, setView] = useState('landing');
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);
  // Feb 2026 — Landing-view global search. Filters ALL orders (branch +
  // customer) by item code / customer name / branch name / order#.
  // While empty, the two landing tiles stay visible; once non-empty
  // the tiles hide and a Search Results panel appears below with each
  // matching order as an expandable card (items table inline). Deep-
  // dive actions still live in the branch/customer drilldowns — the
  // "Open full order" button jumps the user there.
  const [globalSearch, setGlobalSearch] = useState('');
  const [expandedSearchIds, setExpandedSearchIds] = useState({});
  // Which order cards have their Pick List panel expanded. Default = closed
  // for every order — user reveals it by clicking the chevron.
  const [pickListOpen, setPickListOpen] = useState({});
  // Per-order refs to `PickListSection` so the parent can imperatively
  // trigger inline-edit mode from Allocation Details' "Edit
  // allocations" button — keeps a single edit surface + shared plan
  // state across the two views.
  const pickListRefs = React.useRef({});
  // Feb 2026 — Which order cards have their Allocation Details panel
  // expanded. Same collapsed-by-default pattern as pickListOpen.
  const [allocationDetailsOpen, setAllocationDetailsOpen] = useState({});
  // Order id to briefly highlight after a successful scan/lookup. Cleared
  // by a timer so the green ring fades on its own.
  const [highlightId, setHighlightId] = useState(null);
  // Feb 2026 — order currently open in the Manual Allocate modal.
  // Null when the modal is closed.
  const [manualAllocOrder, setManualAllocOrder] = useState(null);
  const [loadingPicturesOrder, setLoadingPicturesOrder] = useState(null);
  // Feb 2026 — inline order editing. Fetched from /admin/me on mount.
  // Super admin (or a sub-admin flagged `allow_order_inline_editing`)
  // sees an Edit items button on non-terminal order cards.
  const [canInlineEdit, setCanInlineEdit] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  // Feb 2026 — Bulk-delete received documents / loading photos gates.
  const [canDeleteReceived, setCanDeleteReceived] = useState(false);
  const [canDeleteLoading, setCanDeleteLoading] = useState(false);
  // Feb 2026 — Post-Pickup Proof edit gate. True for super admin OR
  // any user holding a warehouse role of picker / floor_incharge /
  // manager. Read-only mode (just thumbnails + pallet qty display)
  // when false.
  const [canEditPostPickup, setCanEditPostPickup] = useState(false);
  // Feb 2026 — inline order-creation button gated by the pre-existing
  // `orders.create` permission slug. Super admin is always allowed.
  // The `New Order` button below the page title is only rendered when
  // this flag is true.
  const [canCreateOrders, setCanCreateOrders] = useState(false);
  // Feb 2026 — Manual Allocate gate. Super admin bypasses; sub-admins
  // need the dotted `warehouse.manual_allocate` permission. UI hides
  // the button AND the backend endpoint rejects unauthorized calls
  // with a 403 (defense in depth).
  const [canManualAllocate, setCanManualAllocate] = useState(false);
  const [customersList, setCustomersList] = useState([]);
  const [newOrderOpen, setNewOrderOpen] = useState(false);
  const [newCustomerOrderOpen, setNewCustomerOrderOpen] = useState(false);
  const [newExportOrderOpen, setNewExportOrderOpen] = useState(false);
  // Feb 2026 — Local vs Export toggle in the Customer Orders view.
  // Only visible to admins with `orders.create`. Governs which modal
  // opens when the "New Order" button is clicked; storefront customers
  // never see it because it lives on the admin page.
  const [customerOrderMode, setCustomerOrderMode] = useState('local'); // 'local' | 'export'
  // { [orderId]: {items: [...], dirty: bool, saving: bool} } — one draft
  // per order, kept in memory only. Persist happens on Save.
  const [editingDrafts, setEditingDrafts] = useState({});
  // Product catalog for the "Add item" picker inside the editor.
  const [catalogProducts, setCatalogProducts] = useState([]);
  // Feb 2026 — track which `status='new'` orders the admin has already
  // opened so the pulsing red NEW badge quiets down after the first
  // view. Persisted in localStorage so a refresh doesn't restart the
  // pulse for orders the admin already inspected.
  const [seenNewOrders, setSeenNewOrders] = useState(() => {
    if (typeof window === 'undefined') return {};
    try {
      const raw = window.localStorage.getItem('mr_admin_seen_new_orders');
      return raw ? JSON.parse(raw) : {};
    } catch (_) { return {}; }
  });
  const markOrderSeen = (orderId) => {
    setSeenNewOrders((prev) => {
      if (prev[orderId]) return prev;
      const next = { ...prev, [orderId]: Date.now() };
      try { window.localStorage.setItem('mr_admin_seen_new_orders', JSON.stringify(next)); } catch (_) { /* localStorage full or blocked — safe to ignore */ }
      return next;
    });
  };
  const rowRefs = useRef({});

  const load = async () => {
    try {
      const [{ data: orderData }, { data: driverData }, brResp, meResp, prodResp, custResp, pfResp] = await Promise.all([
        axios.get(`${API}/admin/orders`, { headers: authHeaders }),
        axios.get(`${API}/admin/drivers`, { headers: authHeaders }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/branches`, { headers: authHeaders }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/me`, { headers: authHeaders }).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/products`, { headers: authHeaders }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/customers`, { headers: authHeaders }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/dashboard/order-picking-flags`, { headers: authHeaders }).catch(() => ({ data: { flags: {} } })),
      ]);
      setOrders(orderData);
      setPickingFlags((pfResp?.data?.flags) || {});
      setDrivers(driverData);
      // `/api/admin/branches` returns a raw array; older code paths
      // sometimes wrap it in `{branches: [...]}`. Handle both shapes.
      setBranches(Array.isArray(brResp?.data) ? brResp.data : (brResp?.data?.branches || []));
      const isSuper = !!meResp.data?.is_super_admin;
      setIsSuperAdmin(isSuper);
      setCanInlineEdit(!!meResp.data?.allow_order_inline_editing || isSuper);
      setCanDeleteReceived(isSuper || !!meResp.data?.allow_delete_received_documents);
      setCanDeleteLoading(isSuper || !!meResp.data?.allow_delete_loading_photos);
      // Feb 2026 — Post-Pickup Proof edit gate mirrors backend
      // `_require_role(["manager","floor_incharge","picker"])`.
      const whRoles = Array.isArray(meResp.data?.warehouse_roles) ? meResp.data.warehouse_roles : [];
      setCanEditPostPickup(
        isSuper
        || whRoles.includes('manager')
        || whRoles.includes('floor_incharge')
        || whRoles.includes('picker'),
      );
      // Feb 2026 — `orders.create` permission gate. Super admin has
      // wildcard, sub-admins need the checkbox flipped in Admin Users.
      const perms = Array.isArray(meResp.data?.permissions) ? meResp.data.permissions : [];
      const hasCreatePerm = isSuper || perms.includes('*') || perms.includes('orders') || perms.includes('orders.create');
      setCanCreateOrders(hasCreatePerm);
      // Feb 2026 (per ops sign-off) — Manual Allocate is now gated by
      // the dotted permission `warehouse.manual_allocate`. Super admin
      // and the wildcard `*` bypass. Sub-admins without the specific
      // permission see the button hidden AND the backend endpoint
      // rejects them with a 403 (defense in depth).
      const hasManualAllocPerm = isSuper
        || perms.includes('*')
        || perms.includes('warehouse')
        || perms.includes('warehouse.manual_allocate');
      setCanManualAllocate(hasManualAllocPerm);
      setCatalogProducts(Array.isArray(prodResp.data) ? prodResp.data : []);
      setCustomersList(Array.isArray(custResp.data) ? custResp.data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Auto-refresh disabled per user request — Live polling reserved for
    // the public Live Stock report. Use the manual refresh button or
    // navigate away/back to fetch fresh data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Trigger the item-only order-summary PDF (no prices). `window.open` on
   * a top-level navigation cannot attach the Authorization header — the
   * backend endpoint accepts the admin JWT via `?token=<jwt>` too (same
   * flex auth as the pick-list PDF). Reads the token from localStorage
   * (`mr_admin_token`) — same key used by the pick-list Print anchor.
   */
  const handlePrintOrder = (o) => {
    if (typeof window === 'undefined') return;
    const token = window.localStorage?.getItem('mr_admin_token') || '';
    if (!token) return;
    const url = `${API}/admin/orders/${o.id}/order-summary.pdf?token=${encodeURIComponent(token)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const updateStatus = async (o, status, { allowShortPalletShip = false } = {}) => {
    // Feb 2026 — Pallet-quantity gate is ENFORCED BY THE BACKEND
    // (`/admin/orders/{id}/status` returns 400 with a "pallet
    // quantities" message when the loader hasn't saved). We rely on
    // the backend as the source of truth because AdminOrders caches
    // orders on mount only — a client-side precheck against the local
    // `o.pallets_sent_by_type` was firing false-positives whenever
    // the loader saved pallets on Floor Loading and returned here
    // without refreshing the page. The error catch below intercepts
    // the backend 400 and surfaces the same friendly dialog.
    try {
      const body = { status };
      if (allowShortPalletShip) body.allow_short_pallet_ship = true;
      await axios.put(`${API}/admin/orders/${o.id}/status`, body, { headers: authHeaders });
      toast({ title: 'Status updated', description: `${orderPartyPrimary(o)} → ${status}` });
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Short-pallet 409 — backend blocked the ship because one or
      // more pallets physically holds less than this order's plan.
      // Open the override dialog so super admin can approve a short
      // ship (actual qty used) or ops can fix the count first.
      if (
        err.response?.status === 409
        && detail
        && typeof detail === 'object'
        && detail.code === 'ship_short_pallet'
      ) {
        setShortPalletDialog({
          order: o,
          targetStatus: status,
          shortPallets: Array.isArray(detail.short_pallets) ? detail.short_pallets : [],
          message: typeof detail.message === 'string' ? detail.message : '',
        });
        return;
      }
      // Backend enforces the pallet gate — mirror its 400 response
      // into the friendly dialog so ops sees a clear message rather
      // than a toast.
      if (
        err.response?.status === 400
        && typeof detail === 'string'
        && detail.toLowerCase().includes('pallet quantities')
      ) {
        setGateDialog({
          title: 'Pallet quantity required',
          message: 'Pallet quantity has not been entered. Please enter and save pallet quantities in the floor loading screen before marking this order as picked up.',
        });
        return;
      }
      toast({ title: 'Update failed', description: (typeof detail === 'string' ? detail : detail?.message) || 'Try again' });
    }
  };

  // ---- Inline order editing helpers (Feb 2026) ----
  //
  // `editingDrafts[orderId]` = { items: [...], dirty: bool, saving: bool }.
  // Absent = the order is not currently in edit mode. `openEditor(o)`
  // seeds the draft from `o.items`; `cancelEditor(o)` drops it.
  const _INLINE_EDIT_BLOCKED = ['picked_up', 'out_for_delivery', 'delivered', 'failed', 'cancelled'];
  const isEditBlocked = (o) => _INLINE_EDIT_BLOCKED.includes((o.status || '').toLowerCase());
  const draftFor = (orderId) => editingDrafts[orderId];
  const openEditor = (o) => {
    setEditingDrafts((prev) => ({
      ...prev,
      [o.id]: {
        items: (o.items || []).map((i) => ({ ...i })),
        dirty: false,
        saving: false,
      },
    }));
  };
  const cancelEditor = (orderId) => {
    setEditingDrafts((prev) => {
      const next = { ...prev };
      delete next[orderId];
      return next;
    });
  };
  const patchDraftItem = (orderId, idx, patch) => {
    setEditingDrafts((prev) => {
      const draft = prev[orderId];
      if (!draft) return prev;
      const items = draft.items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
      return { ...prev, [orderId]: { ...draft, items, dirty: true } };
    });
  };
  const removeDraftItem = (orderId, idx) => {
    setEditingDrafts((prev) => {
      const draft = prev[orderId];
      if (!draft) return prev;
      const items = draft.items.filter((_, i) => i !== idx);
      return { ...prev, [orderId]: { ...draft, items, dirty: true } };
    });
  };
  const addDraftItem = (orderId, product) => {
    setEditingDrafts((prev) => {
      const draft = prev[orderId];
      if (!draft) return prev;
      const existingIdx = draft.items.findIndex((it) => it.id === product.id);
      const items =
        existingIdx >= 0
          ? draft.items.map((it, i) =>
              i === existingIdx ? { ...it, qty: (it.qty || 0) + 1 } : it,
            )
          : [
              ...draft.items,
              {
                id: product.id,
                name: product.name,
                size: product.size || '',
                size_type: product.size_type || 'size',
                sku: product.sku || '',
                price: Number(product.price || 0),
                qty: 1,
                image: product.image || '',
                vat_applicable: !!product.vat_applicable,
                uom: product.uom || 'ctn',
              },
            ];
      return { ...prev, [orderId]: { ...draft, items, dirty: true } };
    });
  };
  const saveInlineEdits = async (o) => {
    const draft = draftFor(o.id);
    if (!draft || !draft.dirty) return;
    // Client-side guards mirror the server (better UX — no round-trip
    // for obvious mistakes).
    if (!draft.items.length) {
      toast({ title: 'Add at least one item', description: 'An order cannot be empty.' });
      return;
    }
    for (const it of draft.items) {
      if (Number(it.qty) <= 0) {
        toast({ title: 'Invalid quantity', description: `Quantity for '${it.name}' must be > 0.` });
        return;
      }
    }
    setEditingDrafts((prev) => ({ ...prev, [o.id]: { ...prev[o.id], saving: true } }));
    try {
      await axios.patch(
        `${API}/admin/orders/${o.id}/inline-edit`,
        { items: draft.items.map((it) => ({
            id: it.id, name: it.name, size: it.size || '',
            size_type: it.size_type || 'size',
            price: Number(it.price || 0), qty: Number(it.qty || 0),
            image: it.image || '', vat_applicable: !!it.vat_applicable,
            uom: it.uom || 'ctn',
          })),
        },
        { headers: authHeaders },
      );
      cancelEditor(o.id);
      toast({ title: 'Order edited', description: 'Awaiting branch/customer confirmation.' });
      await load();
    } catch (err) {
      setEditingDrafts((prev) => ({ ...prev, [o.id]: { ...prev[o.id], saving: false } }));
      toast({ title: 'Save failed', description: err.response?.data?.detail || 'Try again.' });
    }
  };

  // Regenerate the FEFO pick list for one order. Fires the same backend
  // planner that also powers the manual Allocate action. Warehouse
  // staff pull a fresh plan whenever they've re-placed stock, so we
  // never surface stale placement ids in the pick list.
  const regeneratePickList = async (o) => {
    try {
      await axios.post(`${API}/admin/orders/${o.id}/dispatch/recalculate`, {}, { headers: authHeaders });
      toast({ title: 'Pick list regenerated' });
      load();
    } catch (err) {
      toast({ title: 'Regeneration failed', description: err.response?.data?.detail || 'Try again' });
    }
  };

  // Reverse the allocation for one order. Releases every locked
  // placement quantity back to the available pool and wipes the
  // dispatch_plan so the order's `allocated_qty` reads 0. Confirms
  // first so an admin can't misclick and lose an assembled pick list.
  const unallocateOrder = async (o) => {
    const ok = window.confirm(
      `Unallocate order ${o.order_number || o.id}?\n\n`
      + `This releases ${Number(o.allocated_qty || 0)} unit(s) of allocated stock back to available inventory and clears the pick list. `
      + `The order will not be automatically re-allocated — a warehouse user must trigger allocation manually.`,
    );
    if (!ok) return;
    try {
      await axios.post(`${API}/admin/orders/${o.id}/dispatch/unallocate`, {}, { headers: authHeaders });
      toast({ title: 'Allocation reversed', description: `${orderPartyPrimary(o)} — stock returned to available.` });
      load();
    } catch (err) {
      toast({ title: 'Unallocate failed', description: err.response?.data?.detail || 'Try again' });
    }
  };

  const assignDriver = async (o, driverId) => {
    try {
      await axios.post(`${API}/admin/orders/${o.id}/assign`, { driver_id: driverId }, { headers: authHeaders });
      toast({ title: 'Driver assigned' });
      load();
    } catch (err) {
      toast({ title: 'Assign failed', description: err.response?.data?.detail || 'Try again' });
    }
  };

  const autoSuggest = async (o) => {
    try {
      const { data } = await axios.get(`${API}/admin/orders/${o.id}/suggest-driver`, { headers: authHeaders });
      if (!data.driver) {
        toast({ title: 'No drivers available', description: data.reason });
        return;
      }
      if (window.confirm(`Assign ${data.driver.name}?\n\n${data.reason}`)) {
        await assignDriver(o, data.driver.id);
      }
    } catch (err) {
      toast({ title: 'Suggest failed' });
    }
  };

  const remove = async (o) => {    if (!window.confirm(`Delete order from ${orderPartyPrimary(o)}?`)) return;
    try {
      await axios.delete(`${API}/admin/orders/${o.id}`, { headers: authHeaders });
      toast({ title: 'Order deleted' });
      load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err.response?.data?.detail || 'Try again' });
    }
  };

  const downloadInvoice = async (o) => {
    try {
      const res = await axios.get(`${API}/orders/${o.id}/invoice`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${o.order_number || o.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'Download failed', description: 'Please try again.' });
    }
  };

  /**
   * Feb 2026 — Delivery Note PDF download. Mirrors the order-summary
   * PDF layout (items + pack + uom + qty) and appends a Pallet
   * Ledger footer block: pallets sent with this order, unit price
   * (latest GRN), total pallet amount, and branch pallet balance
   * after the order. Backend: `GET /api/admin/orders/{id}/delivery-note.pdf`.
   *
   * Availability gate — the DN is a picking-slip-style handoff
   * document, meaningful only after picking is complete and before
   * dispatch. The button stays visible for every order status so
   * the affordance is discoverable, but clicking it on a non-
   * `picked_up` order raises a dialog instead of triggering the
   * download.
   */
  const downloadDeliveryNote = async (o) => {
    if ((o?.status || '') !== 'picked_up') {
      setGateDialog({
        title: 'Delivery Note not available',
        message: 'Delivery Note is only available once the order is picked up.',
      });
      return;
    }
    try {
      const res = await axios.get(`${API}/admin/orders/${o.id}/delivery-note.pdf`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `delivery-note-${o.order_number || o.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'Delivery note download failed', description: err.response?.data?.detail || 'Please try again.' });
    }
  };

  /**
   * Feb 2026 — Loading Sheet PDF download. Sourced from
   * `order.dispatch_plan.pick_lines`, so it shows the exact
   * batches/pallets warehouse staff should load: item code, item name,
   * prod date, expiry, qty and pallet #. Works for both customer and
   * branch orders through a single unified endpoint. Backend returns
   * HTTP 409 with a friendly detail if the order hasn't been
   * allocated yet — surfaced as a toast.
   */
  const downloadLoadingSheet = async (o, format = 'pdf') => {
    const ext = format === 'xlsx' ? 'xlsx' : 'pdf';
    const mime = ext === 'xlsx'
      ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      : 'application/pdf';
    try {
      const res = await axios.get(`${API}/admin/orders/${o.id}/loading-sheet.${ext}`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `loading-sheet-${o.order_number || o.id.slice(0, 8)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      // With `responseType: 'blob'` the backend error body arrives as a
      // Blob — parse it so the 409 "allocate first" detail surfaces
      // instead of a generic failure toast.
      let msg = 'Please try again.';
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || msg;
        } catch { /* leave default */ }
      } else if (data?.detail) {
        msg = data.detail;
      }
      toast({ title: 'Loading Sheet download failed', description: msg });
    }
  };


  // Feb-2026 v5 — view-aware filtering & counts.
  //
  // Branch orders = any order with a `branch_account_id`. Customer
  // orders = everything else. "Pending" excludes terminal statuses
  // (delivered / failed / cancelled).
  const PENDING_STATUSES = ['draft', 'new', 'confirmed', 'assigned', 'picked_up', 'out_for_delivery'];
  const isPending = (o) => PENDING_STATUSES.includes(o.status);
  // Feb-2026 (later) — a "new-ish" order is either status=='new' OR
  // any status whose `created_at` is within the last 24h. This keeps
  // the pulsing red NEW badge (and the tile-level NEW count) visible
  // for a full day even after ops flips the order to `confirmed`, so
  // a fast-moving desk can still see what dropped last shift.
  const NEW_ORDER_GRACE_MS = 24 * 60 * 60 * 1000;
  const isRecentlyNew = (o) => {
    if (!o) return false;
    if (o.status === 'new') return true;
    const t = Date.parse(o.created_at || '');
    return Number.isFinite(t) && (Date.now() - t) < NEW_ORDER_GRACE_MS;
  };
  const branchOrders   = useMemo(() => orders.filter((o) => !!o.branch_account_id), [orders]);
  const customerOrders = useMemo(() => orders.filter((o) => !o.branch_account_id), [orders]);
  const branchPendingCount   = useMemo(() => branchOrders.filter(isPending).length, [branchOrders]);
  const customerPendingCount = useMemo(() => customerOrders.filter(isPending).length, [customerOrders]);
  // Feb-2026 v6 — NEW counts (status='new' OR created in last 24h) so
  // the landing tiles can surface "3 new" alongside the pending total.
  const branchNewCount   = useMemo(() => branchOrders.filter(isRecentlyNew).length, [branchOrders]);
  const customerNewCount = useMemo(() => customerOrders.filter(isRecentlyNew).length, [customerOrders]);
  // Per-branch pending counts. Uses `branch_account_id` on the order
  // as the pivot; branches with zero pending orders still surface a
  // tile so admins can drill in and see the historical list.
  const branchPendingByBid = useMemo(() => {
    const m = {};
    for (const o of branchOrders) if (isPending(o)) m[o.branch_account_id] = (m[o.branch_account_id] || 0) + 1;
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchOrders]);
  // Feb-2026 v6 — per-branch NEW count (same 24h-grace rule) so each
  // tile can render a small red pill next to its main pending number.
  const branchNewByBid = useMemo(() => {
    const m = {};
    for (const o of branchOrders) if (isRecentlyNew(o)) m[o.branch_account_id] = (m[o.branch_account_id] || 0) + 1;
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchOrders]);
  // Feb-2026 v6 — per-branch status breakdown ({bid: {new: n, confirmed: n, …}})
  // so each branch tile can render "new: 3 · confirmed: 5 · delivered: 12"
  // instead of a single collapsed pending number. Terminal statuses
  // (failed / cancelled) roll into a single "closed" bucket to keep the
  // tile compact.
  const branchStatusByBid = useMemo(() => {
    const m = {};
    for (const o of branchOrders) {
      const bid = o.branch_account_id;
      if (!bid) continue;
      const s = o.status || 'draft';
      m[bid] = m[bid] || {};
      m[bid][s] = (m[bid][s] || 0) + 1;
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchOrders]);

  // Base list the current view will render — a branch drill-down
  // filters to that branch, Customer view drops branch orders, and
  // the landing/tile screens don't need a list at all.
  const viewScopedOrders = useMemo(() => {
    if (view === 'branch' && selectedBranchId) {
      return branchOrders.filter((o) => o.branch_account_id === selectedBranchId);
    }
    if (view === 'customer') return customerOrders;
    return orders;
  }, [view, selectedBranchId, orders, branchOrders, customerOrders]);

  // Feb 2026 — per-branch category allow-list lookup so the InlineOrder
  // "Add item" dropdown only surfaces products the branch was allowed
  // to see when it placed the order. Empty list = branch sees the full
  // catalog (matches `/api/branch/categories`'s empty-list-means-all
  // rule). Customer orders (no branch_account_id) never scope down.
  const branchAllowedById = useMemo(() => {
    const m = {};
    for (const b of branches || []) {
      const slugs = Array.isArray(b?.allowed_category_slugs)
        ? b.allowed_category_slugs.map((s) => String(s || '').trim().toLowerCase()).filter(Boolean)
        : [];
      m[b.id] = slugs;
    }
    return m;
  }, [branches]);

  // Feb 2026 — Branch lookup by id for the landing-view global search
  // panel (matches on branch name / username and displays the branch
  // name on each result card).
  const branchesById = useMemo(() => {
    const m = {};
    for (const b of branches || []) m[b.id] = b;
    return m;
  }, [branches]);

  // Feb 2026 — Landing-view global search matcher. Returns [] when the
  // query is empty. Matching (case-insensitive `includes`):
  //   • order.order_number
  //   • customer/business name  (orderPartyPrimary/Contact)
  //   • order.customer_name  (raw fallback)
  //   • branch name / username (via branch_account_id → branchesById)
  //   • any item's sku, product id, or name (covers "item code" match)
  const searchMatches = useMemo(() => {
    const q = globalSearch.trim().toLowerCase();
    if (!q) return [];
    return orders.filter((o) => {
      const primary = String(orderPartyPrimary(o) || '').toLowerCase();
      const contact = String(orderPartyContact(o) || '').toLowerCase();
      const rawCust = String(o?.customer_name || '').toLowerCase();
      const onum    = String(o?.order_number || '').toLowerCase();
      const b       = branchesById[o?.branch_account_id];
      const bname   = String(b?.name || '').toLowerCase();
      const buser   = String(b?.username || '').toLowerCase();
      if (
        primary.includes(q) || contact.includes(q) || rawCust.includes(q)
        || onum.includes(q) || bname.includes(q) || buser.includes(q)
      ) return true;
      for (const it of (o?.items || [])) {
        const sku  = String(it?.sku || '').toLowerCase();
        const nm   = String(it?.name || '').toLowerCase();
        const iid  = String(it?.id || '').toLowerCase();
        if (sku.includes(q) || nm.includes(q) || iid.includes(q)) return true;
      }
      return false;
    });
  }, [globalSearch, orders, branchesById]);

  const catalogForOrder = (o) => {
    const bid = o?.branch_account_id;
    if (!bid) return catalogProducts;                  // customer order → no scope-down
    const allowed = branchAllowedById[bid];
    if (!allowed || allowed.length === 0) return catalogProducts; // empty list = ALL visible
    const set = new Set(allowed);
    return catalogProducts.filter(
      (p) => set.has(String(p?.category_slug || '').trim().toLowerCase()),
    );
  };

  const filtered = filter === 'all' ? viewScopedOrders : viewScopedOrders.filter((o) => o.status === filter);

  /**
   * Open an order found via the search bar:
   *   1) Reset the status filter to "all" so the row is in the rendered list.
   *   2) Expand the row.
   *   3) Scroll smoothly to it.
   *   4) Apply a 2.5s emerald ring highlight so the user sees which row
   *      matched (the list can be long after a status filter reset).
   */
  const focusOrder = (order) => {
    if (!order?.id) return;
    setFilter('all');
    setExpanded(order.id);
    setHighlightId(order.id);
    // Defer scroll so the row exists in the DOM after the filter reset.
    setTimeout(() => {
      const el = rowRefs.current[order.id];
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 50);
    // Fade the highlight after a moment.
    setTimeout(() => setHighlightId((id) => (id === order.id ? null : id)), 2500);
  };

  /**
   * Server-side fallback for orders not in the currently-loaded list
   * (e.g., when more than 2000 orders exist). Reloads the admin order
   * list and re-scans for the order number. We deliberately don't add
   * a dedicated single-order lookup endpoint — orders/{id} expects a
   * UUID, not an order_number, and adding a parallel endpoint would
   * duplicate access-control logic. A list refresh covers the rare
   * edge case cleanly.
   */
  const lookupOrderNumberRemote = async (orderNumber) => {
    try {
      const { data } = await axios.get(`${API}/admin/orders`, { headers: authHeaders });
      setOrders(data);
      const upper = orderNumber.toUpperCase();
      return data.find((o) => (o.order_number || '').toUpperCase() === upper) || null;
    } catch {
      return null;
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 lg:p-10">
        <div className="mb-8 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-extrabold text-[#0F172A] font-display">
              Orders
            </h1>
            <p className="text-neutral-500 mt-1">Manage and fulfill customer orders.</p>
          </div>
          {canCreateOrders && (
            <button
              type="button"
              onClick={() => setNewOrderOpen(true)}
              className="inline-flex items-center gap-2 rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white font-semibold text-sm px-4 py-2 shadow-sm transition"
              data-testid="admin-orders-new-order-btn"
            >
              <Plus className="w-4 h-4" />
              New Order
            </button>
          )}
        </div>

        {/* --- Landing tiles --------------------------------------- */}
        {view === 'landing' && (
          <>
            {/* Feb 2026 — Global search across ALL orders (branch +
                customer). Matches on item code / SKU, customer name,
                branch name, and order#. Empty query → tiles show as
                before. Non-empty → tiles hide and a results panel
                appears below with each matching order as a compact
                expandable card. */}
            <div className="mb-6 max-w-3xl" data-testid="admin-orders-global-search">
              <div className="relative">
                <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  value={globalSearch}
                  onChange={(e) => setGlobalSearch(e.target.value)}
                  placeholder="Search all orders by item code, customer, or branch name…"
                  className="w-full rounded-full border border-neutral-300 bg-white pl-9 pr-9 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40 focus:border-[#0078D4]"
                  data-testid="admin-orders-global-search-input"
                  autoComplete="off"
                  spellCheck="false"
                />
                {globalSearch && (
                  <button
                    type="button"
                    onClick={() => { setGlobalSearch(''); setExpandedSearchIds({}); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700 p-1 rounded-full"
                    data-testid="admin-orders-global-search-clear"
                    aria-label="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              {globalSearch.trim() && (
                <div className="mt-2 text-xs text-neutral-500" data-testid="admin-orders-global-search-count">
                  {searchMatches.length} order{searchMatches.length === 1 ? '' : 's'} match
                </div>
              )}
            </div>

            {globalSearch.trim() ? (
              /* --- Search results panel ------------------------- */
              <div className="space-y-3 max-w-4xl" data-testid="admin-orders-global-search-results">
                {searchMatches.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-neutral-200 p-10 text-center" data-testid="admin-orders-global-search-empty">
                    <Search className="w-8 h-8 text-neutral-300 mx-auto mb-3" />
                    <p className="text-neutral-500 text-sm">
                      No orders match <span className="font-mono font-semibold text-neutral-700">"{globalSearch}"</span>.
                    </p>
                    <p className="text-neutral-400 text-xs mt-1">Try an item code, customer name, branch name, or order number.</p>
                  </div>
                ) : (
                  searchMatches.map((o) => {
                    const isOpen = !!expandedSearchIds[o.id];
                    const b = branchesById[o.branch_account_id];
                    const branchLabel = b ? (b.name || b.username || '') : '';
                    return (
                      <div
                        key={o.id}
                        className="bg-white rounded-2xl border border-neutral-200 overflow-hidden"
                        data-testid={`admin-orders-global-search-card-${o.id}`}
                      >
                        <div
                          className="p-4 flex flex-wrap items-center gap-3 cursor-pointer hover:bg-neutral-50"
                          onClick={() => setExpandedSearchIds((prev) => ({ ...prev, [o.id]: !prev[o.id] }))}
                        >
                          <div className="flex-1 min-w-[180px]">
                            <div className="font-semibold text-[#0F172A] flex items-center gap-2 flex-wrap">
                              <span data-testid={`admin-orders-global-search-primary-${o.id}`}>{orderPartyPrimary(o)}</span>
                              {o.order_number && (
                                <span className="text-xs font-mono font-normal text-[#0078D4]">
                                  · {o.order_number}
                                </span>
                              )}
                              {isSuperAdmin && <SystemOrderIdPill orderId={o.id} testIdSuffix={`search-${o.id}`} />}
                            </div>
                            {orderPartyContact(o) && (
                              <div className="text-[11px] text-neutral-500 mt-0.5 truncate" title={`Contact: ${orderPartyContact(o)}`}>
                                Contact: {orderPartyContact(o)}
                              </div>
                            )}
                            <div className="text-[11px] text-neutral-500 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                              <span>{formatDateTime(o.created_at)}</span>
                              {branchLabel && (
                                <span className="inline-flex items-center gap-1" data-testid={`admin-orders-global-search-branch-${o.id}`}>
                                  <Building2 className="w-3 h-3 text-neutral-400" />
                                  {branchLabel}
                                </span>
                              )}
                              {!branchLabel && (
                                <span className="inline-flex items-center gap-1 text-emerald-700">
                                  <Users className="w-3 h-3" />
                                  Customer
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-xs text-neutral-600 tabular-nums">
                            {(o.items || []).reduce((s, i) => s + Number(i?.qty || 0), 0)} items
                          </div>
                          {showTotal && (
                            <div className="font-bold text-[#0F172A] tabular-nums text-sm">
                              <Rial /> {Number(o.total || 0).toFixed(3)}
                            </div>
                          )}
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${statusColors[o.status] || 'bg-neutral-200 text-neutral-700'}`}>
                            {o.status}
                          </span>
                          <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        </div>
                        {isOpen && (
                          <div
                            className="border-t border-neutral-100 bg-neutral-50/60 px-4 py-4"
                            data-testid={`admin-orders-global-search-details-${o.id}`}
                          >
                            <div className="overflow-x-auto">
                              <table className="min-w-full text-xs">
                                <thead>
                                  <tr className="text-left text-[10px] uppercase tracking-wider text-neutral-500 border-b border-neutral-200">
                                    <th className="py-1.5 pr-3 font-semibold w-10 text-right tabular-nums">#</th>
                                    <th className="py-1.5 pr-3 font-semibold">Item code</th>
                                    <th className="py-1.5 pr-3 font-semibold">Name</th>
                                    <th className="py-1.5 pr-3 font-semibold text-right">Qty</th>
                                    <th className="py-1.5 pr-3 font-semibold">UOM</th>
                                    {showItemsAmount && (
                                      <th className="py-1.5 pr-3 font-semibold text-right">Price</th>
                                    )}
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-neutral-200">
                                  {(o.items || []).map((it, idx) => (
                                    <tr key={`${o.id}-${it?.id || idx}`}>
                                      <td className="py-1.5 pr-3 text-right tabular-nums text-neutral-500">{(it?.line_no != null && it?.line_no !== '') ? it.line_no : (idx + 1)}</td>
                                      <td className="py-1.5 pr-3 font-mono text-[#0078D4]">{it?.sku || '—'}</td>
                                      <td className="py-1.5 pr-3 text-[#0F172A]">
                                        {it?.name}
                                        {it?.size ? <span className="text-neutral-500"> · {it.size}</span> : null}
                                      </td>
                                      <td className="py-1.5 pr-3 text-right tabular-nums">{Number(it?.qty || 0)}</td>
                                      <td className="py-1.5 pr-3 text-neutral-600">{it?.uom || '—'}</td>
                                      {showItemsAmount && (
                                        <td className="py-1.5 pr-3 text-right tabular-nums">
                                          <Rial /> {Number(it?.price || 0).toFixed(3)}
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                              {showAddress && o.address && (
                                <div className="text-[11px] text-neutral-600">
                                  <span className="text-neutral-500">Address:</span> {o.address}
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (o.branch_account_id) {
                                    setView('branch');
                                    setSelectedBranchId(o.branch_account_id);
                                  } else {
                                    setView('customer');
                                  }
                                  setFilter('all');
                                  setGlobalSearch('');
                                  setExpandedSearchIds({});
                                  setExpanded(o.id);
                                  setHighlightId(o.id);
                                  setTimeout(() => {
                                    const el = rowRefs.current[o.id];
                                    if (el?.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                  }, 80);
                                  setTimeout(() => setHighlightId((id) => (id === o.id ? null : id)), 2500);
                                }}
                                className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white text-xs font-semibold px-3 py-1.5 transition"
                                data-testid={`admin-orders-global-search-open-${o.id}`}
                              >
                                Open full order <ChevronDown className="w-3.5 h-3.5 -rotate-90" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 max-w-3xl" data-testid="admin-orders-landing">
            <button
              type="button"
              onClick={() => { setView('branch'); setSelectedBranchId(null); setFilter('all'); }}
              className="text-left bg-white rounded-2xl border border-neutral-200 hover:border-[#0078D4] hover:shadow-lg transition p-6 group"
              data-testid="admin-orders-tile-branch"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#DEECF9] flex items-center justify-center">
                  <Store className="w-6 h-6 text-[#0078D4]" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Branch Orders</div>
                  <div className="text-lg font-bold text-[#0F172A]">Fulfil orders raised by branches</div>
                </div>
              </div>
              <div className="mt-5 flex items-baseline gap-2">
                <span className="text-4xl font-extrabold text-[#0F172A] tabular-nums" data-testid="admin-orders-tile-branch-count">
                  {branchPendingCount}
                </span>
                <span className="text-sm text-neutral-500">pending</span>
                {branchNewCount > 0 && (
                  <span
                    className="ml-auto inline-flex items-center gap-1 rounded-full bg-red-600 text-white px-2 py-0.5 text-[10px] font-black tracking-widest uppercase animate-pulse"
                    data-testid="admin-orders-tile-branch-new-count"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-white/90" />
                    {branchNewCount} New
                  </span>
                )}
              </div>
            </button>
            <button
              type="button"
              onClick={() => { setView('customer'); setFilter('all'); }}
              className="text-left bg-white rounded-2xl border border-neutral-200 hover:border-[#0078D4] hover:shadow-lg transition p-6 group"
              data-testid="admin-orders-tile-customer"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <Users className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Customer Orders</div>
                  <div className="text-lg font-bold text-[#0F172A]">Direct orders from customers</div>
                </div>
              </div>
              <div className="mt-5 flex items-baseline gap-2">
                <span className="text-4xl font-extrabold text-[#0F172A] tabular-nums" data-testid="admin-orders-tile-customer-count">
                  {customerPendingCount}
                </span>
                <span className="text-sm text-neutral-500">pending</span>
                {customerNewCount > 0 && (
                  <span
                    className="ml-auto inline-flex items-center gap-1 rounded-full bg-red-600 text-white px-2 py-0.5 text-[10px] font-black tracking-widest uppercase animate-pulse"
                    data-testid="admin-orders-tile-customer-new-count"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-white/90" />
                    {customerNewCount} New
                  </span>
                )}
              </div>
            </button>
          </div>
            )}
          </>
        )}

        {/* --- Per-branch tiles ------------------------------------ */}
        {view === 'branch' && !selectedBranchId && (
          <div data-testid="admin-orders-branch-tiles">
            <button
              type="button"
              onClick={() => { setView('landing'); setFilter('all'); }}
              className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-600 hover:text-[#0F172A]"
              data-testid="admin-orders-back-to-landing"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Orders home
            </button>
            {branches.length === 0 ? (
              <div className="bg-white rounded-2xl border border-neutral-200 p-10 text-center text-neutral-500">
                No branches yet. Add branches under Admin → Branches.
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {branches
                  .slice()
                  .sort((a, b) => (branchPendingByBid[b.id] || 0) - (branchPendingByBid[a.id] || 0)
                    || String(a.name || '').localeCompare(String(b.name || '')))
                  .map((b) => {
                    const pending = branchPendingByBid[b.id] || 0;
                    const newCnt = branchNewByBid[b.id] || 0;
                    // Per-status breakdown — Feb-2026 v6. Compact pill
                    // strip beneath the branch name so the desk can see
                    // "new: 3 · confirmed: 5 · delivered: 12" at a glance
                    // without drilling in. Only non-zero buckets render;
                    // draft rolls up with `new` because it's visually
                    // identical to end users. Terminal statuses
                    // (`failed`/`cancelled`) collapse into a single grey
                    // "closed" bucket.
                    const stats = branchStatusByBid[b.id] || {};
                    const breakdown = [
                      { key: 'new',              label: 'new',        n: (stats.new || 0) + (stats.draft || 0), color: 'text-red-600 bg-red-50 border-red-100' },
                      { key: 'confirmed',        label: 'confirmed',  n: stats.confirmed || 0,                  color: 'text-[#0078D4] bg-[#DEECF9] border-[#0078D4]/20' },
                      { key: 'assigned',         label: 'assigned',   n: stats.assigned || 0,                   color: 'text-purple-700 bg-purple-50 border-purple-100' },
                      { key: 'picked_up',        label: 'picked',     n: stats.picked_up || 0,                  color: 'text-indigo-700 bg-indigo-50 border-indigo-100' },
                      { key: 'out_for_delivery', label: 'out',        n: stats.out_for_delivery || 0,           color: 'text-amber-700 bg-amber-50 border-amber-100' },
                      { key: 'delivered',        label: 'delivered',  n: stats.delivered || 0,                  color: 'text-emerald-700 bg-emerald-50 border-emerald-100' },
                      { key: 'closed',           label: 'closed',     n: (stats.failed || 0) + (stats.cancelled || 0), color: 'text-neutral-500 bg-neutral-100 border-neutral-200' },
                    ].filter((row) => row.n > 0);
                    return (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => { setSelectedBranchId(b.id); setFilter('all'); }}
                        className={`text-left bg-white rounded-2xl border transition p-4 group ${pending > 0 ? 'border-[#0078D4]/40 hover:shadow-lg' : 'border-neutral-200 hover:border-neutral-300'}`}
                        data-testid={`admin-orders-branch-tile-${b.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${pending > 0 ? 'bg-[#DEECF9]' : 'bg-neutral-100'}`}>
                            <Building2 className={`w-5 h-5 ${pending > 0 ? 'text-[#0078D4]' : 'text-neutral-400'}`} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-[#0F172A] truncate flex items-center gap-2" data-testid={`admin-orders-branch-tile-name-${b.id}`}>
                              <span className="truncate">{b.name || b.username || '—'}</span>
                              {newCnt > 0 && (
                                <span
                                  className="inline-flex items-center gap-1 rounded-full bg-red-600 text-white px-1.5 py-0.5 text-[9px] font-black tracking-widest uppercase animate-pulse shrink-0"
                                  data-testid={`admin-orders-branch-tile-new-${b.id}`}
                                >
                                  <span className="w-1 h-1 rounded-full bg-white/90" />
                                  {newCnt}
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-neutral-500 truncate">{b.username}</div>
                          </div>
                          <div
                            className={`text-2xl font-extrabold tabular-nums ${pending > 0 ? 'text-[#0078D4]' : 'text-neutral-300'}`}
                            data-testid={`admin-orders-branch-tile-count-${b.id}`}
                          >
                            {pending}
                          </div>
                        </div>
                        {breakdown.length > 0 && (
                          <div
                            className="mt-3 flex flex-wrap gap-1"
                            data-testid={`admin-orders-branch-tile-breakdown-${b.id}`}
                          >
                            {breakdown.map((row) => (
                              <span
                                key={row.key}
                                className={`inline-flex items-center gap-1 rounded-full border ${row.color} px-2 py-0.5 text-[10px] font-semibold tabular-nums`}
                                data-testid={`admin-orders-branch-tile-breakdown-${b.id}-${row.key}`}
                              >
                                {row.label}: {row.n}
                              </span>
                            ))}
                          </div>
                        )}
                      </button>
                    );
                  })}
              </div>
            )}
          </div>
        )}

        {/* --- Order list (Customer view OR branch drill-down) ----- */}
        {(view === 'customer' || (view === 'branch' && selectedBranchId)) && (
          <>
            <div className="flex items-center justify-between gap-3 mb-4">
              <button
                type="button"
                onClick={() => {
                  if (view === 'branch' && selectedBranchId) {
                    setSelectedBranchId(null);  // back to the branch tiles
                  } else {
                    setView('landing');
                    setFilter('all');
                  }
                }}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-600 hover:text-[#0F172A]"
                data-testid="admin-orders-back-btn"
              >
                <ArrowLeft className="w-4 h-4" />
                {view === 'branch' && selectedBranchId ? 'Back to branches' : 'Back to Orders home'}
              </button>
              {view === 'customer' && canCreateOrders && (
                <div className="flex items-center gap-3">
                  <div
                    className="inline-flex rounded-full bg-neutral-100 p-0.5 border border-neutral-200"
                    role="tablist"
                    data-testid="customer-order-type-toggle"
                  >
                    <button
                      type="button"
                      role="tab"
                      aria-selected={customerOrderMode === 'local'}
                      onClick={() => setCustomerOrderMode('local')}
                      className={`px-3 py-1 rounded-full text-xs font-semibold transition ${customerOrderMode === 'local' ? 'bg-white text-[#0F172A] shadow-sm' : 'text-neutral-600 hover:text-[#0F172A]'}`}
                      data-testid="customer-order-type-toggle-local"
                    >
                      Local Customers
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={customerOrderMode === 'export'}
                      onClick={() => setCustomerOrderMode('export')}
                      className={`px-3 py-1 rounded-full text-xs font-semibold transition ${customerOrderMode === 'export' ? 'bg-white text-[#0F172A] shadow-sm' : 'text-neutral-600 hover:text-[#0F172A]'}`}
                      data-testid="customer-order-type-toggle-export"
                    >
                      Export Customers
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (customerOrderMode === 'export') {
                        setNewExportOrderOpen(true);
                      } else {
                        setNewCustomerOrderOpen(true);
                      }
                    }}
                    className="inline-flex items-center gap-2 rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white font-semibold text-sm px-4 py-2 shadow-sm transition"
                    data-testid="admin-orders-new-customer-order-btn"
                  >
                    <Plus className="w-4 h-4" />
                    New Order
                  </button>
                </div>
              )}
            </div>

            {view === 'branch' && selectedBranchId && (
              <div className="mb-4 flex items-center gap-2 text-sm text-neutral-600">
                <Building2 className="w-4 h-4 text-[#0078D4]" />
                <span>
                  Showing orders for{' '}
                  <span className="font-semibold text-[#0F172A]" data-testid="admin-orders-selected-branch-name">
                    {(branches.find((br) => br.id === selectedBranchId) || {}).name || '—'}
                  </span>
                </span>
              </div>
            )}

            {/* Search / scan */}
            <AdminOrderSearch
              orders={viewScopedOrders}
              onFound={focusOrder}
              lookupRemote={lookupOrderNumberRemote}
            />

            {/* Filter chips */}
            <div className="flex flex-wrap gap-2 mb-6">
              <button
                onClick={() => setFilter('all')}
                data-testid="orders-filter-chip-all"
                className={`px-4 py-1.5 rounded-full text-sm font-semibold ${
                  filter === 'all' ? 'bg-[#0F172A] text-white' : 'bg-white border border-neutral-200 text-neutral-700'
                }`}
              >
                All ({viewScopedOrders.length})
              </button>
              {/* Feb 2026 — "Ready to load" quick-jump. The standalone
                  Warehouses > Loading tab was retired; managers now use
                  this chip to land straight on the loading queue
                  (auto-scoped to status=picked_up). */}
              {(() => {
                const readyCount = viewScopedOrders.filter((o) => o.status === 'picked_up').length;
                return (
                  <button
                    onClick={() => setFilter('picked_up')}
                    data-testid="orders-filter-chip-ready-to-load"
                    className={`px-4 py-1.5 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 ${
                      filter === 'picked_up'
                        ? 'bg-amber-500 text-white shadow-sm'
                        : 'bg-amber-50 border border-amber-200 text-amber-800 hover:bg-amber-100'
                    }`}
                    title="Orders that finished picking and are queued for loading"
                  >
                    <PackageOpen className="w-3.5 h-3.5" />
                    Ready to load ({readyCount})
                  </button>
                );
              })()}
              {STATUSES.map((s) => {
                const count = viewScopedOrders.filter((o) => o.status === s).length;
                return (
                  <button
                    key={s}
                    onClick={() => setFilter(s)}
                    data-testid={`orders-filter-chip-${s}`}
                    className={`px-4 py-1.5 rounded-full text-sm font-semibold capitalize ${
                      filter === s ? 'bg-[#0078D4] text-white' : 'bg-white border border-neutral-200 text-neutral-700'
                    }`}
                  >
                    {s} ({count})
                  </button>
                );
              })}
            </div>

            {/* Orders */}
            <div className="space-y-3">
          {loading ? (
            <div className="text-neutral-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="bg-white rounded-2xl border border-neutral-200 p-12 text-center">
              <Package className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
              <p className="text-neutral-500">No orders to show.</p>
            </div>
          ) : (
            filtered.map((o) => {
              const isOpen = expanded === o.id;
              const isHighlight = highlightId === o.id;
              const accent = getStatusAccent(o.status);
              return (
                <div
                  key={o.id}
                  ref={(el) => { if (el) rowRefs.current[o.id] = el; }}
                  data-order-id={o.id}
                  className={`relative rounded-2xl border border-l-4 overflow-hidden transition-shadow ${accent.border} ${accent.tint} ${
                    isHighlight
                      ? 'border-emerald-400 ring-4 ring-emerald-200'
                      : 'border-neutral-200'
                  }`}
                >
                  {/* Feb 2026 — pulsing red NEW badge on freshly-received
                      orders. Sits as a corner ribbon so it's visible on
                      both desktop and mobile without shifting layout.
                      Once the admin opens the tile, the animation stops
                      (see `.new-order-badge--seen`) but the label stays
                      visible until the order status changes. v6 — the
                      badge now persists for a full 24h after order
                      creation (via `isRecentlyNew`) so a confirmed-but-
                      still-fresh order still reads as NEW to the desk. */}
                  {isRecentlyNew(o) && (
                    <span
                      className={`new-order-badge${seenNewOrders[o.id] ? ' new-order-badge--seen' : ''} absolute top-2 right-2 z-10 inline-flex items-center gap-1 rounded-full bg-red-600 text-white px-2.5 py-0.5 text-[10px] font-black tracking-widest uppercase select-none pointer-events-none`}
                      data-testid={`admin-order-new-badge-${o.id}`}
                      data-seen={seenNewOrders[o.id] ? '1' : '0'}
                      aria-label={seenNewOrders[o.id] ? 'New order' : 'New order — click to review'}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-white/90"></span>
                      New
                    </span>
                  )}
                  <div
                    className="p-5 flex flex-wrap items-center gap-4 cursor-pointer hover:bg-white/40"
                    onClick={() => {
                      if (isRecentlyNew(o)) markOrderSeen(o.id);
                      setExpanded(isOpen ? null : o.id);
                    }}
                  >
                    <div className="flex-1 min-w-[160px]">
                      <div className="font-semibold text-[#0F172A] flex items-center gap-2 flex-wrap">
                        <span data-testid={`order-party-primary-${o.id}`}>{orderPartyPrimary(o)}</span>
                        {o.order_number && (
                          <span
                            className="text-xs font-normal text-neutral-500 tracking-wide"
                            data-testid={`order-number-inline-${o.id}`}
                          >
                            · {o.order_number}
                          </span>
                        )}
                        {pickingFlags[o.id] === 'fully' && (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5"
                            data-testid={`order-picking-chip-${o.id}`}
                            title="Every picking task on this order is complete."
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
                            Fully Picked
                          </span>
                        )}
                        {pickingFlags[o.id] === 'partial' && (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-red-50 border border-red-200 text-red-800 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5"
                            data-testid={`order-picking-chip-${o.id}`}
                            title="Some picking tasks are done — others are still pending."
                          >
                            <span className="relative inline-flex h-1.5 w-1.5" aria-hidden="true">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-60" />
                              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-600" />
                            </span>
                            Partial Picked
                          </span>
                        )}
                        {isSuperAdmin && <SystemOrderIdPill orderId={o.id} testIdSuffix={o.id} />}
                        {o.order_type === 'requisition' && (
                          <span
                            className="inline-block bg-indigo-100 text-indigo-800 border border-indigo-200 rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase"
                            data-testid={`requisition-badge-${o.id}`}
                          >
                            Warehouse Requisition
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-neutral-500 mt-1 flex items-center gap-2 flex-wrap">
                        <span>{formatDateTime(o.created_at)}</span>
                        {showPhoneActions && o.order_type !== 'requisition' && (
                          <PhoneActions phone={o.customer_phone} size="sm" stopPropagation />
                        )}
                        {o.order_type === 'requisition' && o.requested_by && (
                          <span className="text-neutral-500">by <strong className="text-neutral-700">{o.requested_by}</strong></span>
                        )}
                      </div>
                      {/* Feb 2026 — Secondary contact line: shows the
                          individual contact person under the business
                          name (only when they differ). Kept small
                          + muted so ops sees the party identity first. */}
                      {orderPartyContact(o) && (
                        <div
                          className="text-[11px] text-neutral-500 mt-0.5 truncate"
                          data-testid={`order-party-contact-${o.id}`}
                          title={`Contact: ${orderPartyContact(o)}`}
                        >
                          Contact: {orderPartyContact(o)}
                        </div>
                      )}
                      {/* Feb 2026 v10 — comma-joined category list drawn
                          from the order's items. Same treatment as
                          BranchOrders.jsx so a manager scanning the
                          admin pipeline sees the product-mix at a
                          glance without expanding each order. */}
                      {(o.categories || []).length > 0 && (
                        <div
                          className="text-[11px] text-neutral-500 mt-0.5 truncate"
                          data-testid={`admin-order-categories-${o.id}`}
                          title={(o.categories || []).join(', ')}
                        >
                          {(o.categories || []).join(', ')}
                        </div>
                      )}
                      {/* Feb 2026 — Activity-by chips. Short first
                          names for Created / Picked / Loaded /
                          Delivered. Hidden entirely when the backend
                          didn't attach `activity_by` (nothing to say
                          yet) so the card stays uncluttered. */}
                      <OrderActivityByChips activity={o.activity_by} orderId={o.id} />
                    </div>
                    {showItemCount && (
                      <div className="text-sm text-neutral-600">
                        {o.items.reduce((s, i) => s + i.qty, 0)} items
                      </div>
                    )}
                    {/* Allocated qty — Feb 2026 v4. Admin view is
                        unconditional (admins always see every column
                        per the Branch Column Settings contract); the
                        matching column on BranchOrders.jsx is gated by
                        `isHidden("orders.allocated")`. */}
                    <div
                      className="text-xs text-neutral-500 tabular-nums"
                      data-testid={`admin-order-allocated-${o.id}`}
                      title="Total qty allocated to warehouse placements for this order"
                    >
                      <span className="font-semibold text-[#0F172A]">{Number(o.allocated_qty || 0)}</span>
                      <span className="text-neutral-400"> alloc</span>
                    </div>
                    {showTotal && (
                      <div className="font-bold text-[#0F172A]"><Rial /> {o.total.toFixed(3)}</div>
                    )}
                    <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${statusColors[o.status]}`}>
                      {o.status}
                    </span>
                    <ChevronDown className={`w-5 h-5 text-neutral-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </div>

                  {isOpen && (
                    <div className="px-5 pb-5 border-t border-neutral-100 pt-4">
                      {/* Detail-header actions — Print button downloads the
                          items-only order summary PDF (no prices). Sits at the
                          very top so it's always the first CTA visible when an
                          admin drills into an order. */}
                      <div className="flex flex-wrap items-center gap-2 mb-4">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handlePrintOrder(o); }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#0078D4] hover:bg-[#005A9E] text-white transition"
                          data-testid={`admin-print-order-btn-${o.id}`}
                        >
                          <Printer className="w-3.5 h-3.5" /> Print
                        </button>
                        {/* Delivery Note — Feb 2026. Downloads a PDF
                            with the same layout as the order summary
                            (items + pack + uom + qty) plus a Pallet
                            Ledger footer: pallets sent with this
                            order, unit price, total pallet amount and
                            the branch pallet balance after the trip.
                            Always visible for discoverability — the
                            click handler `downloadDeliveryNote` gates
                            on `order.status === 'picked_up'` and
                            raises a dialog when the order isn't
                            picked up yet. */}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); downloadDeliveryNote(o); }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-[#0078D4] text-[#0078D4] bg-white hover:bg-[#DEECF9] transition"
                          data-testid={`admin-delivery-note-btn-${o.id}`}
                          title="Download Delivery Note PDF (available once picked up)"
                        >
                          <Download className="w-3.5 h-3.5" /> Delivery Note
                        </button>
                        {/* Loading Sheet — Feb 2026. Downloads a PDF
                            or Excel file whose item table is pulled
                            from `dispatch_plan.pick_lines` — each row
                            shows the exact production date, expiry,
                            qty and pallet # warehouse staff should
                            load. Works for both customer and branch
                            orders (same endpoint). Backend returns 409
                            if the order isn't allocated yet — toasted
                            by `downloadLoadingSheet`. Two buttons
                            share the same handler, differing only by
                            format arg. */}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); downloadLoadingSheet(o, 'pdf'); }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-[#0078D4] text-[#0078D4] bg-white hover:bg-[#DEECF9] transition"
                          data-testid={`admin-loading-sheet-btn-${o.id}`}
                          title="Download Loading Sheet as PDF (from dispatch_plan.pick_lines)"
                        >
                          <Download className="w-3.5 h-3.5" /> Loading Sheet (PDF)
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); downloadLoadingSheet(o, 'xlsx'); }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-emerald-600 text-emerald-700 bg-white hover:bg-emerald-50 transition"
                          data-testid={`admin-loading-sheet-xlsx-btn-${o.id}`}
                          title="Download Loading Sheet as Excel (.xlsx — same data as PDF)"
                        >
                          <Download className="w-3.5 h-3.5" /> Loading Sheet (Excel)
                        </button>
                        {/* Manual Allocate — opens the ManualAllocateModal
                            where the admin picks source placement + qty
                            per line. Sits next to Auto (Regenerate) so
                            the admin can choose either flow. Feb 2026:
                            gated by `warehouse.manual_allocate` (super
                            admin bypasses). Unauthorized users don't
                            see the button and the backend rejects
                            their calls with 403.

                            Feb 2026 (two-tier lock) — Hidden on
                            `delivered` for everyone. On other
                            terminal statuses (received, shipped,
                            cancelled, …) only hidden for
                            non-super-admins; super admin bypasses. */}
                        {canManualAllocate && !(
                          String(o.status || '').toLowerCase() === 'delivered' ||
                          (!isSuperAdmin && ['picked_up', 'out_for_delivery', 'cancelled', 'failed', 'received', 'completed', 'shipped', 'returned'].includes(String(o.status || '').toLowerCase()))
                        ) && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setManualAllocOrder(o); }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-[#0078D4] text-[#0078D4] bg-[#DEECF9] hover:bg-[#C7E0F4] transition"
                            data-testid={`admin-manual-allocate-btn-${o.id}`}
                            title="Manually allocate this order to specific warehouse placements"
                          >
                            <ListChecks className="w-3.5 h-3.5" /> Manual Allocate
                          </button>
                        )}
                        {/* Re-run Allocation — Feb 2026. Regenerates the
                            FEFO dispatch_plan against the CURRENT free
                            placement quantities. Same underlying endpoint
                            as the collapsible Pick List's "Regenerate"
                            button, but promoted to a top-level action so
                            warehouse ops can pick a fresh plan after a
                            put-away or reconciliation without hunting
                            through nested panels. Hidden for terminal
                            statuses where a plan no longer makes sense. */}
                        {!(['picked_up', 'out_for_delivery', 'delivered', 'cancelled', 'failed', 'received', 'completed'].includes(String(o.status || '').toLowerCase())) && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); regeneratePickList(o); }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-[#0078D4] text-[#0078D4] bg-white hover:bg-[#DEECF9] transition"
                            data-testid={`admin-rerun-allocation-btn-${o.id}`}
                            title="Regenerate the FEFO dispatch plan using current placement free quantities"
                          >
                            <RefreshCw className="w-3.5 h-3.5" /> Re-run Allocation
                          </button>
                        )}
                        {/* Unallocate — only when this order currently
                            holds a warehouse allocation. Reversing is
                            destructive (wipes the pick plan), so we
                            confirm before firing. Feb 2026 (auto-alloc
                            disabled): once released, the order will
                            stay unallocated until a warehouse user hits
                            the Allocate action manually.

                            Feb 2026 (two-tier lock) — Hidden on
                            `delivered` for everyone. On other
                            terminal statuses only hidden for
                            non-super-admins; super admin bypasses. */}
                        {Number(o.allocated_qty || 0) > 0 && !(
                          String(o.status || '').toLowerCase() === 'delivered' ||
                          (!isSuperAdmin && ['picked_up', 'out_for_delivery', 'cancelled', 'failed', 'received', 'completed', 'shipped', 'returned'].includes(String(o.status || '').toLowerCase()))
                        ) && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); unallocateOrder(o); }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100 transition"
                            data-testid={`admin-unallocate-order-btn-${o.id}`}
                            title={`Release ${Number(o.allocated_qty || 0)} unit(s) of allocated stock`}
                          >
                            <Undo2 className="w-3.5 h-3.5" /> Unallocate ({Number(o.allocated_qty || 0)})
                          </button>
                        )}
                        {/* Loading Pictures — Feb 2026. Opens a modal
                            where the admin picks a loader from the
                            order's loading-task assignees and uploads
                            photos on their behalf. Existing loader
                            self-uploads appear grouped alongside
                            admin-attributed ones. */}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setLoadingPicturesOrder(o); }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-[#0078D4] text-[#0078D4] bg-white hover:bg-[#DEECF9] transition"
                          data-testid={`admin-loading-pictures-btn-${o.id}`}
                          title="Upload / review loading proof photos"
                        >
                          <Camera className="w-3.5 h-3.5" /> Loading Pictures
                        </button>
                      </div>
                      {/* Feb 2026 — Super Admin Override banner + edit
                          panel. Renders only when the caller is the
                          super admin (component NO-OPs otherwise). */}
                      <SuperAdminOrderOverride order={o} onSaved={load} />
                      {/* Feb 2026 (user directive) — Layout change:
                          "Items detail need to be UNDER Customer
                          details". Previously the two sections sat
                          side-by-side in a 2-col grid; they now stack
                          vertically so ITEM CODE / NAME / PACK / QTY /
                          AMOUNT can breathe across the full card
                          width. */}
                      <div className="space-y-6">
                        <div>
                          <div className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">Customer details</div>
                          <div className="space-y-1 text-sm">
                            <div>
                              <span className="text-neutral-500">Business:</span>{' '}
                              <span className="font-semibold text-[#0F172A]" data-testid={`order-detail-business-${o.id}`}>{orderPartyPrimary(o)}</span>
                            </div>
                            {orderPartyContact(o) && (
                              <div data-testid={`order-detail-contact-${o.id}`}>
                                <span className="text-neutral-500">Contact:</span> {orderPartyContact(o)}
                              </div>
                            )}
                            {o.is_export_order && (o.export_country_name || o.export_country_code) && (
                              <div
                                className="mt-1 inline-flex items-center gap-1.5 rounded-md bg-amber-50 border border-amber-200 px-2 py-1 text-[11px] font-semibold text-amber-900"
                                data-testid={`order-export-badge-${o.id}`}
                              >
                                <span aria-hidden>🌐</span>
                                Export Order · Ship to <b data-testid={`order-export-country-${o.id}`}>{o.export_country_name || o.export_country_code}</b>
                                {o.export_phone_dial_code && (
                                  <span className="text-amber-800/80">· dial <span className="font-mono">{o.export_phone_dial_code}</span></span>
                                )}
                              </div>
                            )}
                            {showPhoneActions && (
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-neutral-500">Phone:</span>
                                <PhoneActions phone={o.customer_phone} />
                              </div>
                            )}
                            {showCustomerEmail && o.customer_email && (
                              <div><span className="text-neutral-500">Email:</span> {o.customer_email}</div>
                            )}
                            {showAddress && o.customer_address && <div><span className="text-neutral-500">Address:</span> {o.customer_address}</div>}
                            {showNotes && o.notes && <div><span className="text-neutral-500">Notes:</span> {o.notes}</div>}
                            {showLocationMap && o.location_lat && o.location_lng && (
                              <div className="flex items-start gap-1.5 pt-1">
                                <MapPin className="w-3.5 h-3.5 mt-0.5 text-[#0078D4] flex-shrink-0" />
                                <div>
                                  {o.location_address && (
                                    <div className="text-neutral-700">{o.location_address}</div>
                                  )}
                                  <a
                                    href={`https://www.google.com/maps?q=${o.location_lat},${o.location_lng}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-xs text-[#0078D4] hover:underline"
                                  >
                                    {o.location_lat.toFixed(5)}, {o.location_lng.toFixed(5)}
                                    {o.location_accuracy ? ` · ±${Math.round(o.location_accuracy)}m` : ''} · view map
                                  </a>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        {showItemsTable && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <div className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Items</div>
                            {o.edit_pending_confirmation && (
                              <span
                                className="text-[10px] bg-amber-100 text-amber-800 border border-amber-300 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider"
                                data-testid={`order-${o.id}-edit-pending-pill`}
                                title="This order was edited by admin/staff and is waiting on branch/customer confirmation."
                              >
                                Edited — awaiting confirmation
                              </span>
                            )}
                            {canInlineEdit && !draftFor(o.id) && !isEditBlocked(o) && (
                              <button
                                type="button"
                                onClick={() => openEditor(o)}
                                className="ml-auto text-[11px] rounded-full border border-[#0078D4] text-[#0078D4] hover:bg-[#0078D4]/5 px-2.5 py-1 font-bold"
                                data-testid={`order-${o.id}-edit-items`}
                              >
                                Edit items
                              </button>
                            )}
                          </div>
                          {draftFor(o.id) ? (
                            <InlineOrderItemsEditor
                              order={o}
                              draft={draftFor(o.id)}
                              onQty={(idx, qty) => patchDraftItem(o.id, idx, { qty })}
                              onPrice={(idx, price) => patchDraftItem(o.id, idx, { price })}
                              onRemove={(idx) => removeDraftItem(o.id, idx)}
                              onAdd={(product) => addDraftItem(o.id, product)}
                              catalog={catalogForOrder(o)}
                              onSave={() => saveInlineEdits(o)}
                              onCancel={() => cancelEditor(o.id)}
                              showPrice={showTotal}
                            />
                          ) : (
                            <div className="space-y-2 text-sm" data-testid={`order-${o.id}-items-table`}>
                              <div className="overflow-x-auto rounded-lg border border-neutral-200">
                                <table className="w-full text-left">
                                  <thead>
                                    <tr className="bg-neutral-50 text-[10px] uppercase tracking-wide text-neutral-500">
                                      <th className="px-3 py-2 font-semibold w-10 text-right tabular-nums">#</th>
                                      <th className="px-3 py-2 font-semibold w-[110px]">Item Code</th>
                                      <th className="px-3 py-2 font-semibold">Item Name</th>
                                      <th className="px-3 py-2 font-semibold w-[110px]">Pack</th>
                                      <th className="px-3 py-2 font-semibold text-right w-[90px]">Qty</th>
                                      {showItemsAmount && (
                                        <th className="px-3 py-2 font-semibold text-right w-[110px]">Amount</th>
                                      )}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {o.items.map((i, idx) => (
                                      <tr key={idx} className="border-t border-neutral-100 align-top" data-testid={`order-${o.id}-items-row-${idx}`}>
                                        <td className="px-3 py-2 text-right tabular-nums text-neutral-500" data-testid={`order-${o.id}-items-line-no-${idx}`}>{(i?.line_no != null && i?.line_no !== '') ? i.line_no : (idx + 1)}</td>
                                        <td className="px-3 py-2 font-mono text-xs text-neutral-600">{i.sku || '—'}</td>
                                        <td className="px-3 py-2 text-[#0F172A]">
                                          {i.name}
                                        </td>
                                        <td className="px-3 py-2 text-neutral-600 tabular-nums" data-testid={`order-${o.id}-items-pack-${idx}`}>
                                          {i.size || '—'}
                                        </td>
                                        <td className="px-3 py-2 tabular-nums text-right whitespace-nowrap">
                                          {i.qty} <span className="text-neutral-500">{i.uom || 'ctn'}</span>
                                        </td>
                                        {showItemsAmount && (
                                          <td className="px-3 py-2 font-medium tabular-nums text-right whitespace-nowrap"><Rial /> {(i.price * i.qty).toFixed(3)}</td>
                                        )}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              {showPricingBreakdown && (
                                <div className="flex justify-between pt-2 font-bold">
                                  <span>Total</span>
                                  <span><Rial /> {o.total.toFixed(3)}</span>
                                </div>
                              )}
                            </div>
                          )}
                          {isSuperAdmin && Array.isArray(o.edit_log) && o.edit_log.length > 0 && !draftFor(o.id) && (
                            <details className="mt-3" data-testid={`order-${o.id}-edit-log`}>
                              <summary className="text-[11px] cursor-pointer text-neutral-500 hover:text-neutral-800 select-none">
                                Edit history ({o.edit_log.length})
                              </summary>
                              <div className="mt-2 space-y-1 max-h-48 overflow-y-auto rounded border border-neutral-200 bg-neutral-50 p-2 text-[11px]">
                                {o.edit_log.map((e, ix) => (
                                  <div key={ix} className="text-neutral-700">
                                    <span className="text-neutral-400">
                                      {formatDateTime(e.ts)}
                                    </span>{' '}
                                    <span className="font-semibold">{e.editor_name}</span>{' '}
                                    <span className="inline-block px-1.5 py-0.5 rounded bg-neutral-200 text-neutral-800 text-[10px] font-bold">{e.action}</span>{' '}
                                    {e.item_name && <span>· {e.item_name}</span>}
                                    {e.old_value !== undefined && e.old_value !== null && (
                                      <span className="text-neutral-500"> · from <code>{typeof e.old_value === 'object' ? JSON.stringify(e.old_value) : String(e.old_value)}</code></span>
                                    )}
                                    {e.new_value !== undefined && e.new_value !== null && (
                                      <span className="text-neutral-500"> → <code>{typeof e.new_value === 'object' ? JSON.stringify(e.new_value) : String(e.new_value)}</code></span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}
                        </div>
                        )}
                      </div>
                      {/* Feb 2026 — Loading Pictures section moved to
                          appear directly ABOVE the Received Documents
                          section (per user request) so proofs of load
                          and proofs of receipt sit next to each other
                          in reading order. */}
                      <div className="mt-5 grid sm:grid-cols-2 gap-4 mb-3">
                        {/* Feb 2026 — per-order WMS Task assignment panel.
                            Rendered next to the Driver panel. Visible to
                            anyone who can view orders (permission drives
                            interactivity — non-super without the
                            `orders.assign_wms_tasks` grant sees the
                            panel in read-only mode). */}
                        {(canAssignWmsTasks || isSuperAdmin) && (
                          <WmsTaskAssignPanel
                            orderId={o.id}
                            API={API}
                            authHeaders={authHeaders}
                            canAssign={canAssignWmsTasks || isSuperAdmin}
                          />
                        )}
                        {showDriverPanel && (
                        <div className="rounded-xl bg-neutral-50 border border-neutral-100 p-3">
                          <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-2 flex items-center gap-1">
                            <Truck className="w-3 h-3" /> Driver
                          </div>
                          {(() => {
                            const assigned = drivers.find((d) => d.id === o.driver_id);
                            return (
                              <div className="flex flex-wrap items-center gap-2">
                                {assigned ? (
                                  <span className="text-sm font-semibold text-[#0F172A]">
                                    {assigned.name}
                                    <span className="text-xs text-neutral-500 ml-1">({assigned.vehicle || assigned.phone})</span>
                                  </span>
                                ) : (
                                  <span className="text-sm text-neutral-500">Not assigned</span>
                                )}
                                {canAssignDriver && (
                                <>
                                <select
                                  value={o.driver_id || ''}
                                  onChange={(e) => e.target.value && assignDriver(o, e.target.value)}
                                  className="ml-auto rounded-full border border-neutral-200 px-3 py-1 text-xs"
                                  data-testid={`assign-driver-${o.id}`}
                                >
                                  <option value="" disabled>{assigned ? 'Reassign…' : 'Assign…'}</option>
                                  {drivers.filter((d) => d.active).map((d) => (
                                    <option key={d.id} value={d.id}>{d.name}</option>
                                  ))}
                                </select>
                                <button
                                  onClick={() => autoSuggest(o)}
                                  className="text-xs font-semibold text-[#0078D4] hover:bg-[#DEECF9] px-2.5 py-1 rounded-full"
                                  data-testid={`auto-assign-${o.id}`}
                                >
                                  Auto
                                </button>
                                </>
                                )}
                                {assigned?.phone && (
                                  <div className="basis-full flex items-center gap-1.5 mt-1">
                                    <a
                                      href={`tel:${assigned.phone}`}
                                      className="text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2.5 py-1 rounded-full inline-flex items-center gap-1"
                                      data-testid={`call-driver-${o.id}`}
                                    >
                                      <Phone className="w-3 h-3" /> Call
                                    </a>
                                    <a
                                      href={`https://wa.me/${assigned.phone.replace(/\D/g, '')}?text=${encodeURIComponent(`Order ${o.order_number}: `)}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2.5 py-1 rounded-full inline-flex items-center gap-1"
                                      data-testid={`whatsapp-driver-${o.id}`}
                                    >
                                      <MessageCircle className="w-3 h-3" /> WhatsApp
                                    </a>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                        )}
                        {showPublicTracker && (
                        <div className="rounded-xl bg-neutral-50 border border-neutral-100 p-3">
                          <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-2">Public tracker</div>
                          <a
                            href={`/track/${o.order_number}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm text-[#0078D4] hover:underline flex items-center gap-1 break-all"
                            data-testid={`track-link-${o.id}`}
                          >
                            <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" /> /track/{o.order_number}
                          </a>
                        </div>
                        )}
                      </div>

                      {/* Pick List — FEFO plan generated on order confirmation.
                          Panel is ALWAYS rendered (mirrors BranchOrders.jsx)
                          so the chevron shows even before a plan exists; body
                          stays collapsed until the admin clicks the header. */}
                      {(o.status !== 'draft' && o.status !== 'cancelled') && (
                        <PickListSection
                          ref={pickListRefs.current[o.id] || (pickListRefs.current[o.id] = React.createRef())}
                          order={o}
                          open={!!pickListOpen[o.id]}
                          onToggle={() => setPickListOpen((prev) => ({ ...prev, [o.id]: !prev[o.id] }))}
                          onRegenerate={() => regeneratePickList(o)}
                          canManualAllocate={canManualAllocate}
                          isSuper={isSuperAdmin}
                          canReassignPicker={canReassignPicker}
                          authHeaders={authHeaders}
                          onSaved={async (updated) => {
                            if (updated) {
                              setOrders((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
                              toast({
                                title: 'Pick list updated',
                                description: `Order ${updated.order_number || (updated.id || '').slice(0, 8)} · ${Number(updated.allocated_qty || 0)} unit(s) locked.`,
                              });
                            } else {
                              await load();
                            }
                          }}
                        />
                      )}

                      {/* Feb 2026 — Allocation Details. Collapsible view
                          of the same dispatch plan enriched with item
                          code, allocated pallet/location, remaining
                          on-hand after this allocation, and shortfall.
                          Refreshes whenever the pick list expands or
                          the plan is regenerated. */}
                      {(o.status !== 'draft' && o.status !== 'cancelled') && (
                        <AllocationDetailsSection
                          order={o}
                          open={!!allocationDetailsOpen[o.id]}
                          onToggle={() => setAllocationDetailsOpen((prev) => ({ ...prev, [o.id]: !prev[o.id] }))}
                          authHeaders={authHeaders}
                          canManualAllocate={canManualAllocate}
                          onEditAllocations={() => {
                            // Open the pick list section and enter edit mode.
                            // A single edit surface keeps the two views in
                            // lock-step because they share order.dispatch_plan.
                            setPickListOpen((prev) => ({ ...prev, [o.id]: true }));
                            setTimeout(() => {
                              pickListRefs.current[o.id]?.current?.startEdit?.();
                            }, 60);
                          }}
                        />
                      )}

                      {/* Feb 2026 — Loading Pictures section (moved
                          here from earlier in the card so it renders
                          directly ABOVE the Received Documents /
                          Loading Photos flat lists per user request).
                          Collapsible + grouped-by-loader + lightbox
                          preview. */}
                      <LoadingPicturesSection
                        orderId={o.id}
                        API={API}
                        authHeaders={authHeaders}
                        testidPrefix={`admin-order-${o.id}-loading-pictures`}
                        isSuperAdmin={isSuperAdmin}
                        canDelete={canDeleteLoading}
                      />

                      {/* Feb 2026 — Post-Pickup Proof section.
                          Lets a picker (and manager / floor_incharge /
                          super admin) add photos + pallet qty AFTER
                          the order crosses into Picked Up. Renders
                          only when `o.status` ∈
                          {picked_up, out_for_delivery, delivered}. */}
                      <PostPickupProofSection
                        order={o}
                        API={API}
                        authHeaders={authHeaders}
                        canEdit={canEditPostPickup}
                        testidPrefix={`admin-order-${o.id}-post-pickup-proof`}
                      />

                      {/* Received documents — files uploaded by the
                          branch when they confirmed receipt. Admin
                          always sees this panel (visibility toggle
                          only affects branch users). */}
                      {(o.received_documents || []).length > 0 && (
                        <ReceivedDocumentsPanel
                          docs={o.received_documents || []}
                          baseUrl={`${API}/admin/orders/${o.id}/received-documents`}
                          authHeaders={authHeaders}
                          testidPrefix={`admin-order-${o.id}`}
                          canDelete={canDeleteReceived}
                          bulkDeleteUrl={`${API}/admin/orders/${o.id}/received-documents/bulk-delete`}
                        />
                      )}

                      {/* Feb 2026 — the legacy flat-list Loading photos
                          panel that used to render here was REMOVED
                          because the collapsible + grouped
                          `LoadingPicturesSection` above already shows
                          every photo with attribution + a lightbox.
                          Keeping both duplicated the same photos twice
                          on the page. The ERP Invoice # chip stays. */}
                      {o.erp_invoice_number && (
                        <div
                          className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800"
                          data-testid={`admin-order-${o.id}-erp-invoice`}
                        >
                          <span className="uppercase tracking-wider text-[10px] mr-2">ERP Invoice #</span>
                          <span className="font-mono">{o.erp_invoice_number}</span>
                        </div>
                      )}

                      {/* Live chat with driver — only when a driver is assigned */}
                      {showChat && o.driver_id && (
                        <div className="mb-3">
                          <OrderChat
                            apiBase={API}
                            orderId={o.id}
                            role="admin"
                            authHeaders={authHeaders}
                            variant="light"
                          />
                        </div>
                      )}
                      <div className="mt-5 flex flex-wrap items-center gap-2">
                        {canEditStatus && (
                        <>
                        <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mr-1">Set status:</span>
                        {STATUSES.map((s) => (
                          <button
                            key={s}
                            onClick={() => updateStatus(o, s)}
                            disabled={o.status === s}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize transition ${
                              o.status === s
                                ? `${statusColors[s]} opacity-60 cursor-default`
                                : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-700'
                            }`}
                          >
                            {s}
                          </button>
                        ))}
                        </>
                        )}
                        {showInvoiceDownload && (
                        <button
                          onClick={() => downloadInvoice(o)}
                          className="text-xs font-semibold text-[#0078D4] hover:bg-[#DEECF9] px-3 py-1.5 rounded-full flex items-center gap-1"
                        >
                          <Download className="w-3.5 h-3.5" /> Invoice PDF
                        </button>
                        )}
                        {canDelete && (
                        <button
                          onClick={() => remove(o)}
                          className="ml-auto text-xs font-semibold text-red-500 hover:bg-red-50 px-3 py-1.5 rounded-full flex items-center gap-1"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
          </>
        )}
      </div>
      {manualAllocOrder && (
        <ErrorBoundary label="manual-allocate">
        <ManualAllocateModal
          order={manualAllocOrder}
          authHeaders={authHeaders}
          onClose={() => setManualAllocOrder(null)}
          onDone={(updatedOrder) => {
            setManualAllocOrder(null);
            toast({
              title: 'Manual allocation saved',
              description: `Order ${updatedOrder.order_number || updatedOrder.id.slice(0, 8)} allocated · ${Number(updatedOrder.allocated_qty || 0)} unit(s) locked.`,
            });
            load();
          }}
        />
        </ErrorBoundary>
      )}
      {loadingPicturesOrder && (
        <LoadingPicturesModal
          order={loadingPicturesOrder}
          onClose={() => setLoadingPicturesOrder(null)}
          onUploaded={() => { /* keep modal open so admin can review + upload more */ }}
        />
      )}
      {newOrderOpen && (
        <NewOrderModal
          customers={customersList}
          products={catalogProducts}
          branches={branches}
          onClose={() => setNewOrderOpen(false)}
          onCreated={async (created) => {
            setNewOrderOpen(false);
            await load();
            // Auto-scroll to the newly created order + briefly highlight.
            if (created?.id) {
              setHighlightId(created.id);
              setTimeout(() => setHighlightId(null), 2500);
            }
          }}
          authHeaders={authHeaders}
        />
      )}
      {newCustomerOrderOpen && (
        <NewCustomerOrderModal
          customers={customersList}
          products={catalogProducts}
          onClose={() => setNewCustomerOrderOpen(false)}
          onCreated={async (created) => {
            setNewCustomerOrderOpen(false);
            await load();
            if (created?.id) {
              setHighlightId(created.id);
              setTimeout(() => setHighlightId(null), 2500);
            }
          }}
          authHeaders={authHeaders}
        />
      )}
      {newExportOrderOpen && (
        <NewExportOrderModal
          products={catalogProducts}
          onClose={() => setNewExportOrderOpen(false)}
          onCreated={async (created) => {
            setNewExportOrderOpen(false);
            await load();
            if (created?.id) {
              setHighlightId(created.id);
              setTimeout(() => setHighlightId(null), 2500);
            }
          }}
          authHeaders={authHeaders}
        />
      )}

      {/* Feb 2026 — Reusable workflow gate dialog. Surfaces both the
          "Delivery Note not available until picked up" gate and the
          "pallet quantities required" gate with the exact user-
          specified copy. Rendered at the end of the layout so it
          overlays every order row without needing per-order state. */}
      <Dialog open={gateDialog !== null} onOpenChange={(open) => { if (!open) setGateDialog(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="order-workflow-gate-dialog">
          <DialogHeader>
            <DialogTitle data-testid="order-workflow-gate-title">
              {gateDialog?.title || 'Action not available'}
            </DialogTitle>
            <DialogDescription data-testid="order-workflow-gate-message" className="pt-2 text-sm text-neutral-700 leading-relaxed">
              {gateDialog?.message}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => setGateDialog(null)}
              className="bg-[#0078D4] hover:bg-[#005A9E] text-white"
              data-testid="order-workflow-gate-ok"
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feb 2026 — Short-pallet shipping guard dialog. Replaces the
          raw JSON toast with a readable per-pallet shortfall list and,
          for super admin, a one-click "Approve short ship" override
          that resubmits the status change with
          `allow_short_pallet_ship: true`. Ops without super admin
          sees the fix-count guidance only. */}
      <Dialog
        open={shortPalletDialog !== null}
        onOpenChange={(open) => { if (!open) { setShortPalletDialog(null); setShortPalletSubmitting(false); } }}
      >
        <DialogContent className="sm:max-w-xl" data-testid="ship-short-pallet-dialog">
          <DialogHeader>
            <DialogTitle data-testid="ship-short-pallet-title">
              Shipping blocked — pallet short on stock
            </DialogTitle>
            <DialogDescription className="pt-2 text-sm text-neutral-700 leading-relaxed" data-testid="ship-short-pallet-message">
              {isSuperAdmin
                ? 'At least one pallet physically holds less than this order\'s pick plan requests. As super admin you can approve a short ship — the actual qty on each pallet below will be used and audited. Otherwise, cancel and fix the physical count or reallocate before shipping.'
                : 'At least one pallet physically holds less than this order\'s pick plan requests. Fix the physical count or reallocate the shortfall before shipping. A super admin can approve a short ship if the pallets really are short.'}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-72 overflow-y-auto border border-neutral-200 rounded-lg divide-y divide-neutral-100" data-testid="ship-short-pallet-list">
            {(shortPalletDialog?.shortPallets || []).map((row, idx) => (
              <div key={row.placement_id || idx} className="px-3 py-2 text-xs" data-testid={`ship-short-pallet-row-${idx}`}>
                <div className="font-semibold text-neutral-900 truncate">
                  {row.product_name || row.product_id || 'Product'}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-neutral-700">
                  <span>Pallet <span className="font-mono">{row.pallet_id || '—'}</span></span>
                  {row.batch_ref ? <span>Batch <span className="font-mono">{row.batch_ref}</span></span> : null}
                  {row.production_date ? <span>Prod {row.production_date}</span> : null}
                  {row.expiry_date ? <span>Exp {row.expiry_date}</span> : null}
                </div>
                <div className="mt-1 flex gap-3 text-neutral-800">
                  <span>Requested <span className="font-semibold">{row.requested_qty}</span></span>
                  <span>On hand <span className="font-semibold">{row.on_hand_qty}</span></span>
                  <span className="text-red-600">Short by <span className="font-semibold">{row.short_by}</span></span>
                </div>
              </div>
            ))}
            {(!shortPalletDialog?.shortPallets || shortPalletDialog.shortPallets.length === 0) && (
              <div className="px-3 py-2 text-xs text-neutral-500">No pallet details returned.</div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setShortPalletDialog(null); setShortPalletSubmitting(false); }}
              data-testid="ship-short-pallet-cancel"
            >
              Cancel
            </Button>
            {isSuperAdmin && (
              <Button
                disabled={shortPalletSubmitting}
                onClick={async () => {
                  if (!shortPalletDialog?.order) return;
                  setShortPalletSubmitting(true);
                  const { order, targetStatus } = shortPalletDialog;
                  try {
                    await updateStatus(order, targetStatus, { allowShortPalletShip: true });
                  } finally {
                    setShortPalletSubmitting(false);
                    setShortPalletDialog(null);
                  }
                }}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="ship-short-pallet-approve"
              >
                {shortPalletSubmitting ? 'Approving…' : 'Approve short ship'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminOrders;

// ============================================================
// Pick List section (July 2026 — surfaced on Order detail row)
// PickListSection — collapsible pick-list panel on the admin order card.
// ============================================================
// Renders the FEFO dispatch plan attached to `order.dispatch_plan`
// (populated by the backend the moment an order transitions to
// "confirmed"). When zone/location/pallet are blank on a pick row,
// they render as "Unassigned" so warehouse staff still know what to
// pick even when the stock hasn't been put away yet. A "Regenerate"
// button re-runs the planner (e.g. after placing new batches).
//
// Feb 2026 v3 — the whole body is hidden behind a chevron toggle
// (mirrors BranchOrders.jsx PickListPanel). The panel is ALWAYS
// rendered so the arrow is visible even before a plan exists —
// clicking it expands the body to either the table or the empty-state
// message. `open` / `onToggle` are controlled by the parent so the
// state survives re-renders driven by refresh/regeneration.
const PickListSection = React.forwardRef(({ order, open, onToggle, onRegenerate, canManualAllocate, isSuper, canReassignPicker, authHeaders, onSaved }, ref) => {
  const plan = order.dispatch_plan;
  // The print endpoint requires an admin JWT. `<a target=_blank>` can't
  // attach an Authorization header, so we append the token as a query
  // param — `require_admin_flex` on the backend accepts either.
  const printToken = (typeof window !== 'undefined' && window.localStorage?.getItem('mr_admin_token')) || '';
  const printUrl = `${API}/admin/orders/${order.id}/dispatch/pick-list${printToken ? `?token=${encodeURIComponent(printToken)}` : ''}`;
  // Feb 2026 — Sort pick rows alphabetically by Location so the
  // on-screen view matches the printed sheet (backend applies the
  // identical sort). Blank/unassigned locations drop to the bottom.
  // `Array.prototype.sort` is stable in modern browsers so pick rows
  // sharing a location keep their original FEFO order.
  const picks = React.useMemo(() => {
    const arr = [...(plan?.pick_lines || [])];
    arr.sort((a, b) => {
      const al = (a.location_id || '').trim().toUpperCase();
      const bl = (b.location_id || '').trim().toUpperCase();
      if (!al && bl) return 1;
      if (al && !bl) return -1;
      if (al === bl) return 0;
      return al < bl ? -1 : 1;
    });
    return arr;
  }, [plan?.pick_lines]);
  const anyShortfall = picks.some((p) => (p.shortfall_qty || 0) > 0);
  const stageWarnings = plan?.stage_warnings || [];
  const hasStageStock = stageWarnings.length > 0;
  const uomByPid = (order.items || []).reduce((acc, it) => {
    if (it.product_id) acc[it.product_id] = it.uom || 'ctn';
    return acc;
  }, {});
  const skuByPid = (order.items || []).reduce((acc, it) => {
    const pid = it.product_id || it.id;
    if (pid) acc[pid] = it.sku || '';
    return acc;
  }, {});
  // Oct 2026 — Persistent line_no lookup, keyed by product_id so a
  // single pick row (or multiple pick rows for the same product_id)
  // can show the "ordered line #" the warehouse staff see on the
  // paper picklist. Falls back to the row index + 1 for legacy
  // orders without a `line_no` stamp.
  const lineNoByPid = (order.items || []).reduce((acc, it, idx) => {
    const pid = it.product_id || it.id;
    if (!pid || acc[pid] !== undefined) return acc;
    acc[pid] = (it.line_no != null && it.line_no !== '') ? it.line_no : (idx + 1);
    return acc;
  }, {});

  // Feb 2026 — Inline edit state. `editing` flips the qty column into
  // editable inputs; `edits` holds the per-row overrides keyed by
  // `${product_id}::${placement_id||'unassigned'}::${batch_ref||''}::${idx}`.
  // Feb 2026 v2 — Users can now also swap a row's location + pallet
  // through a placement picker. `sourcesByPid` caches the response
  // from `/dispatch/available-sources` (same data the ManualAllocate
  // modal uses) so we're re-using well-tested placement math.
  const [editing, setEditing] = React.useState(false);
  const [edits, setEdits] = React.useState({}); // { rowKey: { qty, removed, source_idx } }
  const [saving, setSaving] = React.useState(false);
  const [saveErr, setSaveErr] = React.useState('');
  const [sourcesByPid, setSourcesByPid] = React.useState({}); // { product_id: [{placement_id, warehouse_id, zone, location_id, pallet_id, ...}] }
  const [loadingSources, setLoadingSources] = React.useState(false);
  // Feb 2026 — Live pick status per pallet. Keyed
  // `${product_id}::${pallet_id || 'UNASSIGNED'}::${location_id || 'UNLOCATED'}`
  // (matches the backend grouping) and backed by
  // `/admin/orders/{id}/dispatch/pick-status`. Polled every 5s so
  // split-pallet / split-location picks each render a distinct chip.
  // every 5 seconds while the panel is OPEN so admins / supervisors
  // see Picked / Un-Picked flip the instant a picker completes a
  // pallet, without a manual refresh. The polling is scoped to the
  // single open panel (not every order on screen) so the overhead
  // stays trivial even on long order lists.
  const [pickStatus, setPickStatus] = React.useState({}); // { key: { status, picked_qty, completed_at } }
  React.useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const r = await axios.get(
          `${API}/admin/orders/${order.id}/dispatch/pick-status`,
          { headers: authHeaders },
        );
        if (cancelled) return;
        setPickStatus(r.data?.by_key || {});
      } catch {
        // Silent — a transient failure must not break the panel; the
        // next tick will catch up.
      }
    };
    fetchStatus();
    const iv = setInterval(fetchStatus, 5000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [open, order.id, authHeaders]);
  const statusForRow = React.useCallback((p) => {
    // Feb 2026 — Match the backend grouping which now carries
    // location_id so same-pallet / different-location picking tasks
    // (split pick across two bays) each resolve to their own chip.
    const key = `${p.product_id}::${(p.pallet_id || '').trim() || 'UNASSIGNED'}::${(p.location_id || '').trim() || 'UNLOCATED'}`;
    return pickStatus[key] || null;
  }, [pickStatus]);

  // Feb 2026 — Inline picker reassignment on each pick-list row.
  // `canInlineReassign` = Super admin ALWAYS, or any sub-admin granted
  // the `warehouse.reassign_picker_inline` slug. When off, assignee
  // name renders read-only. `pickerUsers` holds the available picker
  // list fetched lazily the first time the panel opens.
  const canInlineReassign = !!isSuper || !!canReassignPicker;
  const [pickerUsers, setPickerUsers] = React.useState([]);
  const [editingAssigneeKey, setEditingAssigneeKey] = React.useState(null); // row key currently showing dropdown
  const [savingAssigneeKey, setSavingAssigneeKey] = React.useState(null);
  React.useEffect(() => {
    if (!open || !canInlineReassign || pickerUsers.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/admin/warehouse/tasks/assignable-users`,
          { headers: authHeaders },
        );
        if (cancelled) return;
        setPickerUsers(r.data?.users || []);
      } catch {
        /* non-fatal — dropdown just stays empty */
      }
    })();
    return () => { cancelled = true; };
  }, [open, canInlineReassign, authHeaders, pickerUsers.length]);

  const reassignPicker = async (taskId, rowStatusKey, newEmail) => {
    if (!taskId || !newEmail) return;
    setSavingAssigneeKey(rowStatusKey);
    try {
      const r = await axios.patch(
        `${API}/admin/warehouse/tasks/${taskId}/reassign-picker`,
        { assignee_email: newEmail },
        { headers: authHeaders },
      );
      const t = r.data?.task || null;
      // 2026-10 — The backend now returns 409 if the write didn't
      // persist (task deleted mid-request, concurrent reassign, or
      // the final assignee_email doesn't match what we requested).
      // A 2xx therefore guarantees `t.assignee_email === newEmail`;
      // we use the SERVER'S authoritative values with no fallback
      // so the handheld can never show a picker name that didn't
      // make it into warehouse_tasks.
      if (!t || !t.assignee_email) {
        throw new Error('Server did not return a persisted task.');
      }
      setPickStatus((prev) => ({
        ...prev,
        [rowStatusKey]: {
          ...(prev[rowStatusKey] || {}),
          task_id: taskId,
          assignee_email: t.assignee_email,
          assignee_name: t.assignee_name || t.assignee_email,
        },
      }));
      setEditingAssigneeKey(null);
    } catch (err) {
      // Toast lives on the parent; log + bail so the dropdown stays
      // open for a retry. 409 = write didn't persist (device must
      // keep showing the old picker until the user retries).
      console.error('reassignPicker failed', err?.response?.data || err);
      alert(err?.response?.data?.detail || 'Reassign failed — try again.');
    } finally {
      setSavingAssigneeKey(null);
    }
  };

  const rowKey = (p, idx) => `${p.product_id}::${p.placement_id || 'unassigned'}::${p.batch_ref || ''}::${idx}`;

  const startEdit = async () => {
    setLoadingSources(true);
    setSaveErr('');
    try {
      const r = await axios.get(
        `${API}/admin/orders/${order.id}/dispatch/available-sources`,
        { headers: authHeaders },
      );
      const map = {};
      for (const line of (r.data?.lines || [])) {
        map[line.product_id] = line.sources || [];
      }
      setSourcesByPid(map);
      setEditing(true);
    } catch (err) {
      setSaveErr(err?.response?.data?.detail || 'Failed to load placements.');
    } finally {
      setLoadingSources(false);
    }
  };

  const effectiveQty = (p, idx) => {
    const k = rowKey(p, idx);
    if (edits[k]?.removed) return 0;
    const v = edits[k]?.qty;
    return v === undefined || v === '' ? Number(p.qty_to_pick || 0) : Math.max(0, Number(v) || 0);
  };
  const isRowRemoved = (p, idx) => !!edits[rowKey(p, idx)]?.removed;

  // Resolves the CURRENT source for a row — either the edit-picked
  // one (`source_idx` set) or the original placement (matched into
  // sourcesByPid so we render its label + can send its placement_id
  // on save).
  const currentSource = (p, idx) => {
    const list = sourcesByPid[p.product_id] || [];
    const k = rowKey(p, idx);
    const picked = edits[k]?.source_idx;
    if (picked !== undefined && picked !== '') {
      return list[Number(picked)] || null;
    }
    // Fall back to matching the original pick line to a source (same
    // placement_id or, for unassigned rows, the unassigned bucket).
    if (p.placement_id) {
      return list.find((s) => s.placement_id === p.placement_id) || null;
    }
    return list.find((s) => s.unassigned) || null;
  };

  const setRowQty = (p, idx, v) => setEdits((prev) => ({
    ...prev,
    [rowKey(p, idx)]: { ...(prev[rowKey(p, idx)] || {}), qty: v, removed: false },
  }));
  const setRowSource = (p, idx, sourceIdx) => setEdits((prev) => ({
    ...prev,
    [rowKey(p, idx)]: { ...(prev[rowKey(p, idx)] || {}), source_idx: sourceIdx, removed: false },
  }));
  const toggleRowRemoved = (p, idx) => setEdits((prev) => {
    const k = rowKey(p, idx);
    return { ...prev, [k]: { ...(prev[k] || {}), removed: !prev[k]?.removed } };
  });
  const cancelEdit = () => { setEditing(false); setEdits({}); setSaveErr(''); };

  const saveEdits = async () => {
    setSaving(true); setSaveErr('');
    try {
      const byPid = new Map();
      for (let i = 0; i < picks.length; i += 1) {
        const p = picks[i];
        if (isRowRemoved(p, i)) continue;
        const q = effectiveQty(p, i);
        if (q <= 0) continue;
        const src = currentSource(p, i);
        // If the user chose a fresh source, use its metadata. If we
        // failed to resolve (missing / unavailable placement), fall
        // back to the row's own metadata so we don't corrupt the plan.
        const allocationEntry = src ? {
          placement_id: src.placement_id || null,
          warehouse_id: src.warehouse_id || '',
          batch_ref: src.batch_ref || '',
          production_date: src.production_date || '',
          expiry_date: src.expiry_date || '',
          qty: q,
        } : {
          placement_id: p.placement_id || null,
          warehouse_id: p.warehouse_id || '',
          batch_ref: p.batch_ref || '',
          production_date: p.production_date || '',
          expiry_date: p.expiry_date || '',
          qty: q,
        };
        const arr = byPid.get(p.product_id) || [];
        arr.push(allocationEntry);
        byPid.set(p.product_id, arr);
      }
      const body = {
        lines: Array.from(byPid.entries()).map(([product_id, allocations]) => ({
          product_id, allocations,
        })),
      };
      const { data } = await axios.post(
        `${API}/admin/orders/${order.id}/dispatch/manual-allocate`,
        body,
        { headers: authHeaders },
      );
      if (typeof onSaved === 'function') await onSaved(data?.order || null);
      setEditing(false);
      setEdits({});
    } catch (err) {
      setSaveErr(err?.response?.data?.detail || err?.message || 'Failed to save pick list.');
    } finally {
      setSaving(false);
    }
  };

  // Feb 2026 — Two-tier order lock (matches backend
  // `_order_locked_reason`). `delivered` locks Pick List inline
  // edit for everyone; `received` + other terminal statuses lock
  // it for non-super-admins only. Hiding the Edit button here
  // matches the 409 the backend would return anyway, so users
  // never see a save-then-fail sequence.
  const orderStatus = String(order?.status || '').toLowerCase();
  const isDelivered = orderStatus === 'delivered';
  const isTerminalForNonSuper = [
    'cancelled', 'failed', 'returned',
    'out_for_delivery', 'shipped', 'received', 'completed',
  ].includes(orderStatus);
  const isLockedForEdit = isDelivered || (isTerminalForNonSuper && !isSuper);

  const canEdit = !!canManualAllocate && !!plan && picks.length > 0 && !isLockedForEdit;

  // Expose `startEdit` to the parent so the Allocation Details panel
  // can drive a shared edit session — clicking "Edit allocations"
  // there opens the pick list + enters edit mode, keeping a single
  // source of truth for the edit UI and eliminating drift between
  // the two views.
  React.useImperativeHandle(ref, () => ({
    startEdit: () => { if (canEdit && !editing) return startEdit(); },
  }), [canEdit, editing]);

  // Human-facing dropdown label for a placement.
  const sourceLabel = (s) => {
    if (!s) return '—';
    if (s.unassigned) return `${s.warehouse_name || 'WH'} · unassigned batch ${s.batch_ref || ''}`.trim();
    const loc = [s.zone, s.location_id].filter(Boolean).join('-');
    const pal = s.pallet_id ? `Pallet ${s.pallet_id}` : '';
    const batch = s.batch_ref ? `Batch ${s.batch_ref}` : '';
    const avail = Number.isFinite(s.available_qty) ? `avail ${s.available_qty}` : '';
    return [loc, pal, batch, avail].filter(Boolean).join(' · ');
  };

  return (
    <div className="mb-4 rounded-xl bg-neutral-50 border border-neutral-200 overflow-hidden" data-testid={`pick-list-section-${order.id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-neutral-100 transition"
        aria-expanded={!!open}
        data-testid={`pick-list-toggle-${order.id}`}
      >
        <Package className="w-4 h-4 text-[#0078D4]" />
        <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Pick List</div>
        <span className="text-[11px] font-medium text-neutral-500 tabular-nums">
          {plan ? (picks.length === 0 ? '(empty)' : `(${picks.length} line${picks.length === 1 ? '' : 's'})`) : '(not generated)'}
        </span>
        {plan?.generated_at && (
          <span className="text-[10px] text-neutral-400 font-mono ml-1">
            {formatDateTime(plan.generated_at)}
          </span>
        )}
        {anyShortfall && (
          <span className="inline-flex items-center gap-0.5 text-red-700 text-[10px] font-bold ml-1">
            <AlertTriangle className="w-3 h-3" /> shortfall
          </span>
        )}
        {hasStageStock && (
          <span
            className="inline-flex items-center gap-0.5 text-amber-800 text-[10px] font-bold ml-1"
            data-testid={`pick-list-stage-chip-${order.id}`}
          >
            <AlertTriangle className="w-3 h-3" /> STAGE
          </span>
        )}
        <ChevronDown className={`w-4 h-4 text-neutral-500 ml-auto transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="border-t border-neutral-200 p-3" data-testid={`pick-list-body-${order.id}`}>
          <div className="flex items-center justify-end gap-1.5 mb-2">
            {canEdit && !editing && (
              <button
                type="button"
                onClick={startEdit}
                disabled={loadingSources}
                className="text-[11px] font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-60 px-2.5 py-1 rounded-full flex items-center gap-1 border border-emerald-200"
                data-testid={`pick-list-edit-${order.id}`}
                title="Inline-edit pick line qty / location / pallet"
              >
                {loadingSources ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Edit2 className="w-3 h-3" />}
                {loadingSources ? 'Loading…' : 'Edit'}
              </button>
            )}
            {editing && (
              <>
                <button
                  type="button"
                  onClick={saveEdits}
                  disabled={saving}
                  className="text-[11px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-neutral-300 px-2.5 py-1 rounded-full flex items-center gap-1"
                  data-testid={`pick-list-save-${order.id}`}
                >
                  {saving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                  Save
                </button>
                <button
                  type="button"
                  onClick={cancelEdit}
                  disabled={saving}
                  className="text-[11px] font-semibold text-neutral-700 hover:bg-neutral-100 px-2.5 py-1 rounded-full flex items-center gap-1 border border-neutral-300"
                  data-testid={`pick-list-cancel-${order.id}`}
                >
                  Cancel
                </button>
              </>
            )}
            <button
              type="button"
              onClick={onRegenerate}
              className="text-[11px] font-semibold text-[#0078D4] hover:bg-[#DEECF9] px-2.5 py-1 rounded-full flex items-center gap-1"
              data-testid={`pick-list-regenerate-${order.id}`}
            >
              <RefreshCw className="w-3 h-3" /> Regenerate
            </button>
            <a
              href={printUrl}
              target="_blank"
              rel="noreferrer"
              className="text-[11px] font-semibold text-[#0078D4] hover:bg-[#DEECF9] px-2.5 py-1 rounded-full flex items-center gap-1"
              data-testid={`pick-list-print-${order.id}`}
            >
              <Printer className="w-3 h-3" /> Print
            </a>
          </div>
          {saveErr && (
            <div className="mb-2 text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded-md px-2 py-1" data-testid={`pick-list-save-err-${order.id}`}>
              {saveErr}
            </div>
          )}

          {!plan && (
            <div className="text-xs text-neutral-500 py-2" data-testid={`pick-list-empty-${order.id}`}>
              No pick list available yet. It generates automatically when the order status is set to <strong>confirmed</strong>.
              <button
                type="button"
                onClick={onRegenerate}
                className="ml-2 text-[#0078D4] font-semibold hover:underline"
                data-testid={`pick-list-generate-now-${order.id}`}
              >
                Generate now
              </button>
            </div>
          )}

          {plan && picks.length === 0 && (
            <div className="text-xs text-neutral-500 py-2">This order has no line items to pick.</div>
          )}

          {hasStageStock && (
            <div
              className="mb-2 text-[11px] text-amber-900 bg-amber-50 border border-amber-300 rounded-md px-2.5 py-1.5"
              data-testid={`pick-list-stage-warning-${order.id}`}
            >
              <div className="flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <div>
                  <div className="font-bold">STAGE stock in this plan — confirm put-away before dispatching picker</div>
                  <ul className="list-disc pl-4 mt-0.5 space-y-0.5">
                    {stageWarnings.map((w) => (
                      <li key={w.product_id} data-testid={`pick-list-stage-warning-item-${order.id}-${w.product_id}`}>
                        <b>{w.stage_qty}</b> units of <b>{w.product_name || w.product_id}</b> are in STAGE and may not be physically put away yet
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
          {plan && picks.length > 0 && (
            <div className="overflow-x-auto -mx-3">
              <table className="w-full text-xs" data-testid={`pick-list-table-${order.id}`}>
                <thead className="bg-white/60 text-neutral-500 text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="text-right px-2 py-1.5 w-10">#</th>
                    <th className="text-left px-3 py-1.5">Item Code</th>
                    <th className="text-left px-3 py-1.5">Product</th>
                    <th className="text-left px-3 py-1.5">Prod / Expiry</th>
                    <th className="text-left px-3 py-1.5">Warehouse</th>
                    <th className="text-left px-3 py-1.5">Zone</th>
                    <th className="text-left px-3 py-1.5">Location</th>
                    <th className="text-left px-3 py-1.5">Pallet</th>
                    <th className="text-left px-3 py-1.5">UOM</th>
                    <th className="text-right px-3 py-1.5">Qty to pick</th>
                    {/* Feb 2026 — Live pick status chip. Reflects
                        warehouse_tasks.status for the matching pallet.
                        Hidden while the row is in inline-edit mode
                        (status becomes stale as the admin reshuffles
                        placements). */}
                    {!editing && (
                      <th className="text-center px-3 py-1.5">Pick</th>
                    )}
                    {!editing && (
                      <th className="text-left px-3 py-1.5">Picker</th>
                    )}
                    {editing && <th className="px-2 py-1.5" />}
                  </tr>
                </thead>
                <tbody>
                  {picks.map((p, idx) => {
                    const isShort = (p.shortfall_qty || 0) > 0;
                    const isUnassigned = !p.zone && !p.location_id && !p.pallet_id;
                    const removed = isRowRemoved(p, idx);
                    const rowQty = effectiveQty(p, idx);
                    return (
                      <tr
                        key={idx}
                        className={`border-t border-neutral-200 ${
                          removed ? 'bg-neutral-100 line-through text-neutral-400'
                            : isShort ? 'bg-red-50' : ''
                        }`}
                        data-testid={`pick-list-row-${order.id}-${idx}`}
                      >
                        <td
                          className="px-2 py-1.5 text-right tabular-nums text-neutral-500 font-semibold"
                          data-testid={`pick-list-line-no-${order.id}-${idx}`}
                        >
                          {lineNoByPid[p.product_id] ?? (idx + 1)}
                        </td>
                        <td
                          className={`px-3 py-1.5 font-mono text-[11px] ${skuByPid[p.product_id] ? 'text-[#0F172A]' : 'text-neutral-400 italic'}`}
                          data-testid={`pick-list-item-code-${order.id}-${idx}`}
                        >
                          {skuByPid[p.product_id] || '—'}
                        </td>
                        <td className="px-3 py-1.5 font-medium text-[#0F172A]">{p.product_name || p.product_id}</td>
                        <td className="px-3 py-1.5 text-neutral-600">
                          {formatDate(p.production_date) || '—'}
                          {p.expiry_date && (
                            <span className="text-neutral-400"> / exp {formatDate(p.expiry_date)}</span>
                          )}
                        </td>
                        <td className="px-3 py-1.5">
                          {p.warehouse_name || <span className="text-neutral-400 italic">Unassigned</span>}
                        </td>
                        {editing ? (
                          <td className="px-3 py-1.5" colSpan={3}>
                            <select
                              value={
                                edits[rowKey(p, idx)]?.source_idx !== undefined
                                  ? edits[rowKey(p, idx)].source_idx
                                  : (() => {
                                      const list = sourcesByPid[p.product_id] || [];
                                      const foundIdx = list.findIndex(
                                        (s) => (p.placement_id && s.placement_id === p.placement_id) ||
                                               (!p.placement_id && s.unassigned),
                                      );
                                      return foundIdx >= 0 ? String(foundIdx) : '';
                                    })()
                              }
                              onChange={(e) => setRowSource(p, idx, e.target.value)}
                              disabled={removed}
                              className="w-full text-xs font-mono border border-emerald-300 rounded px-1.5 py-0.5 disabled:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                              data-testid={`pick-list-source-select-${order.id}-${idx}`}
                            >
                              <option value="">— pick a location / pallet —</option>
                              {(sourcesByPid[p.product_id] || []).map((s, sIdx) => (
                                <option key={sIdx} value={String(sIdx)}>
                                  {sourceLabel(s)}
                                </option>
                              ))}
                            </select>
                          </td>
                        ) : (
                          <>
                            <td className={`px-3 py-1.5 ${p.zone ? 'font-mono' : 'text-neutral-400 italic'}`}>
                              {p.zone || 'Unassigned'}
                            </td>
                            <td className={`px-3 py-1.5 ${p.location_id ? 'font-mono' : 'text-neutral-400 italic'}`}>
                              {p.location_id || 'Unassigned'}
                            </td>
                            <td className={`px-3 py-1.5 ${p.pallet_id ? 'font-mono' : 'text-neutral-400 italic'}`}>
                              {p.pallet_id || 'Unassigned'}
                            </td>
                          </>
                        )}
                        <td className="px-3 py-1.5 text-neutral-600 font-medium">
                          {uomByPid[p.product_id] || 'ctn'}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums font-semibold">
                          {editing ? (
                            <input
                              type="number"
                              min="0"
                              value={
                                edits[rowKey(p, idx)]?.qty !== undefined
                                  ? edits[rowKey(p, idx)].qty
                                  : (p.qty_to_pick || 0)
                              }
                              onChange={(e) => setRowQty(p, idx, e.target.value)}
                              disabled={removed}
                              className="w-20 text-right px-1.5 py-0.5 border border-neutral-300 rounded text-xs tabular-nums disabled:bg-neutral-100"
                              data-testid={`pick-list-qty-input-${order.id}-${idx}`}
                            />
                          ) : (
                            <>
                              {rowQty}
                              {isShort && (
                                <span className="ml-1 inline-flex items-center gap-0.5 text-red-700 text-[10px] font-bold">
                                  <AlertTriangle className="w-3 h-3" /> short {p.shortfall_qty}
                                </span>
                              )}
                              {!isShort && isUnassigned && p.unassigned && (
                                <span className="ml-1 inline-block bg-amber-100 text-amber-800 rounded-full px-1.5 py-0.5 text-[9px] font-bold">
                                  UNASSIGNED
                                </span>
                              )}
                              {!isShort && p.is_stage && (
                                <span
                                  className="ml-1 inline-block bg-amber-100 text-amber-900 border border-amber-300 rounded-full px-1.5 py-0.5 text-[9px] font-bold"
                                  data-testid={`pick-list-row-stage-${order.id}-${idx}`}
                                  title="Stock booked in STAGE — confirm put-away before picking"
                                >
                                  STAGE
                                </span>
                              )}
                            </>
                          )}
                        </td>
                        {/* Feb 2026 — Live pick status chip. Only
                            rendered when NOT in inline-edit mode so
                            we don't surface stale status while
                            placements are being reshuffled. The
                            poller in `useEffect` above refreshes
                            `pickStatus` every 5 s. */}
                        {!editing && (() => {
                          const st = statusForRow(p);
                          const done = st && st.status === 'completed';
                          const inProg = st && st.status === 'in_progress';
                          const chipCls = done
                            ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                            : inProg
                              ? 'bg-sky-100 text-sky-800 border-sky-200'
                              : 'bg-neutral-100 text-neutral-600 border-neutral-200';
                          const label = done ? 'Picked' : inProg ? 'Picking…' : 'Un-Picked';
                          return (
                            <td className="px-3 py-1.5 text-center">
                              <span
                                className={`inline-flex items-center gap-1 border rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${chipCls}`}
                                data-testid={`pick-list-pick-status-${order.id}-${idx}`}
                                data-status={done ? 'picked' : inProg ? 'in_progress' : 'unpicked'}
                                title={st?.completed_at ? `Picked ${new Date(st.completed_at).toLocaleString()}` : ''}
                              >
                                {done ? <Check className="w-3 h-3" /> : null}
                                {label}
                              </span>
                            </td>
                          );
                        })()}
                        {/* Feb 2026 — Inline Picker column. Mirrors
                            the ASN put-away row dropdown but on the
                            Pick List. Super admin always sees the
                            pencil; sub-admins only when they hold
                            `warehouse.reassign_picker_inline`.
                            Users without the slug see assignee name
                            as plain read-only text. Hidden in inline-
                            edit mode so the row doesn't overflow
                            with controls. */}
                        {!editing && (() => {
                          const st = statusForRow(p);
                          const rowStatusKey = `${p.product_id}::${(p.pallet_id || '').trim() || 'UNASSIGNED'}::${(p.location_id || '').trim() || 'UNLOCATED'}`;
                          const assigneeEmail = (st?.assignee_email || '').trim();
                          const assigneeName = (st?.assignee_name || '').trim() || assigneeEmail;
                          const taskId = st?.task_id || '';
                          const isEditingRow = editingAssigneeKey === rowStatusKey;
                          const isSaving = savingAssigneeKey === rowStatusKey;
                          return (
                            <td
                              className="px-3 py-1.5"
                              data-testid={`pick-list-picker-cell-${order.id}-${idx}`}
                            >
                              {isEditingRow && canInlineReassign ? (
                                <div className="flex items-center gap-1">
                                  <select
                                    autoFocus
                                    disabled={isSaving || !taskId}
                                    defaultValue={assigneeEmail}
                                    onChange={(e) => reassignPicker(taskId, rowStatusKey, e.target.value)}
                                    className="max-w-[180px] rounded-md border border-neutral-300 bg-white px-1.5 py-0.5 text-[11px] focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40 disabled:opacity-60"
                                    data-testid={`pick-list-picker-select-${order.id}-${idx}`}
                                  >
                                    <option value="" disabled>— choose picker —</option>
                                    {pickerUsers.map((u) => (
                                      <option key={u.email} value={u.email}>
                                        {u.name || u.email} · {u.role || '—'}
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    type="button"
                                    onClick={() => setEditingAssigneeKey(null)}
                                    className="text-[10px] text-neutral-500 hover:text-neutral-700"
                                    data-testid={`pick-list-picker-cancel-${order.id}-${idx}`}
                                    title="Cancel"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1.5">
                                  <span
                                    className={`text-[11px] truncate max-w-[140px] ${assigneeName ? 'text-[#0F172A]' : 'text-neutral-400 italic'}`}
                                    title={assigneeEmail || 'Unassigned'}
                                    data-testid={`pick-list-picker-name-${order.id}-${idx}`}
                                  >
                                    {assigneeName || 'Unassigned'}
                                  </span>
                                  {canInlineReassign && taskId && (
                                    <button
                                      type="button"
                                      onClick={() => setEditingAssigneeKey(rowStatusKey)}
                                      className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-neutral-200 bg-white text-neutral-500 hover:text-[#0078D4] hover:border-[#0078D4] transition"
                                      title="Reassign picker"
                                      data-testid={`pick-list-picker-edit-${order.id}-${idx}`}
                                    >
                                      <Edit2 className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                          );
                        })()}
                        {editing && (
                          <td className="px-2 py-1.5 text-right">
                            <button
                              type="button"
                              onClick={() => toggleRowRemoved(p, idx)}
                              title={removed ? 'Undo remove' : 'Remove this pick line'}
                              className={`inline-flex items-center justify-center w-6 h-6 rounded-full border transition ${
                                removed
                                  ? 'bg-neutral-200 border-neutral-300 text-neutral-600 hover:bg-neutral-300'
                                  : 'bg-white border-red-200 text-red-600 hover:bg-red-50'
                              }`}
                              data-testid={`pick-list-row-toggle-remove-${order.id}-${idx}`}
                            >
                              {removed ? <Undo2 className="w-3 h-3" /> : <Trash2 className="w-3 h-3" />}
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {anyShortfall && (
            <div className="mt-2 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-md px-2 py-1.5 flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" />
              One or more lines have insufficient stock — chase replenishment or transfer from another warehouse before dispatch.
            </div>
          )}
        </div>
      )}
    </div>
  );
});
PickListSection.displayName = 'PickListSection';


// ============================================================
// AllocationDetailsSection — collapsible panel on the admin order card.
// Feb 2026 — Renders per-line allocation info from
//   GET /api/admin/orders/{id}/dispatch/allocation-details
// Columns: Item Code, Item Name, Allocated Pallet/Location,
// Allocated Qty, Order #, Remaining on-hand after allocation, Shortfall.
// Auto-refreshes whenever the collapsible is opened or the parent
// order's dispatch_plan.generated_at timestamp changes (i.e. the
// planner was re-run).
// ============================================================
const AllocationDetailsSection = ({ order, open, onToggle, authHeaders, canManualAllocate, onEditAllocations }) => {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [planMayBeStale, setPlanMayBeStale] = React.useState(false);
  const [fillRow, setFillRow] = React.useState(null);
  const [reloadKey, setReloadKey] = React.useState(0);
  const generatedAt = order?.dispatch_plan?.generated_at || null;

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const fetchDetails = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await axios.get(
          `${API}/admin/orders/${order.id}/dispatch/allocation-details`,
          { headers: authHeaders },
        );
        if (!cancelled) {
          setRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
          setPlanMayBeStale(!!res.data?.plan_may_be_stale);
        }
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.detail || 'Failed to load allocation details.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchDetails();
    return () => {
      cancelled = true;
    };
    // Re-fetch when the panel opens or the plan is regenerated
  }, [open, order.id, generatedAt, authHeaders, reloadKey]);

  const hasShortfall = rows.some((r) => (r.shortfall_qty || 0) > 0);
  const totalRows = rows.length;

  return (
    <div
      className="mb-4 rounded-xl bg-neutral-50 border border-neutral-200 overflow-hidden"
      data-testid={`allocation-details-section-${order.id}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-neutral-100 transition"
        aria-expanded={!!open}
        data-testid={`allocation-details-toggle-${order.id}`}
      >
        <ListChecks className="w-4 h-4 text-[#0078D4]" />
        <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">
          Allocation Details
        </div>
        <span className="text-[11px] font-medium text-neutral-500 tabular-nums">
          {order.dispatch_plan
            ? (totalRows === 0 ? '(empty)' : `(${totalRows} line${totalRows === 1 ? '' : 's'})`)
            : '(no plan)'}
        </span>
        {hasShortfall && (
          <span className="inline-flex items-center gap-0.5 text-red-700 text-[10px] font-bold ml-1">
            <AlertTriangle className="w-3 h-3" /> shortfall
          </span>
        )}
        <ChevronDown className={`w-4 h-4 text-neutral-500 ml-auto transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="border-t border-neutral-200 p-3"
          data-testid={`allocation-details-body-${order.id}`}
        >
          {loading && (
            <div
              className="text-xs text-neutral-500 py-2"
              data-testid={`allocation-details-loading-${order.id}`}
            >
              Loading allocation details…
            </div>
          )}
          {!loading && error && (
            <div
              className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-2 py-1.5"
              data-testid={`allocation-details-error-${order.id}`}
            >
              {error}
            </div>
          )}
          {!loading && !error && !order.dispatch_plan && (
            <div
              className="text-xs text-neutral-500 py-2"
              data-testid={`allocation-details-no-plan-${order.id}`}
            >
              No dispatch plan yet — generate a pick list to see allocation details.
            </div>
          )}
          {!loading && !error && order.dispatch_plan && rows.length === 0 && (
            <div className="text-xs text-neutral-500 py-2">
              This order has no allocated items.
            </div>
          )}
          {!loading && !error && planMayBeStale && (
            <div
              className="flex items-center gap-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mb-2"
              data-testid={`allocation-details-stale-${order.id}`}
            >
              <Clock className="w-3.5 h-3.5 flex-shrink-0" />
              Stock has moved since this plan was generated{generatedAt ? ` (${formatDateTime(generatedAt)})` : ''} — figures below may be stale. Recalculate for current numbers.
            </div>
          )}
          {!loading && !error && rows.length > 0 && (
            <>
              {canManualAllocate && !!order.dispatch_plan && (
                <div className="mb-2 flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => onEditAllocations?.()}
                    className="text-[11px] font-semibold text-emerald-700 hover:bg-emerald-50 px-2.5 py-1 rounded-full flex items-center gap-1 border border-emerald-200"
                    data-testid={`allocation-details-edit-${order.id}`}
                    title="Open the Pick List and inline-edit qty, location or pallet on any allocation"
                  >
                    <Edit2 className="w-3 h-3" /> Edit allocations
                  </button>
                </div>
              )}
              <div className="overflow-x-auto -mx-3">
              <table
                className="w-full text-xs"
                data-testid={`allocation-details-table-${order.id}`}
              >
                <thead className="bg-white/60 text-neutral-500 text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-3 py-1.5">Item Code</th>
                    <th className="text-left px-3 py-1.5">Item Name</th>
                    <th className="text-left px-3 py-1.5">Allocated Pallet / Location</th>
                    <th className="text-right px-3 py-1.5">Allocated Qty</th>
                    <th className="text-left px-3 py-1.5">Order #</th>
                    <th className="text-right px-3 py-1.5">Remaining On-hand</th>
                    <th className="text-right px-3 py-1.5">Shortfall</th>
                    {canManualAllocate && (
                      <th className="text-right px-3 py-1.5">Action</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => {
                    const isShort = (r.shortfall_qty || 0) > 0;
                    const isUnassigned = !!r.unassigned || (!r.zone && !r.location_id && !r.pallet_id);
                    return (
                      <tr
                        key={idx}
                        className={`border-t border-neutral-200 ${isShort ? 'bg-red-50' : ''}`}
                        data-testid={`allocation-details-row-${order.id}-${idx}`}
                      >
                        <td
                          className={`px-3 py-1.5 font-mono ${r.item_code ? 'text-[#0F172A]' : 'text-neutral-400 italic'}`}
                          data-testid={`allocation-details-item-code-${order.id}-${idx}`}
                        >
                          {r.item_code || '—'}
                        </td>
                        <td className="px-3 py-1.5 font-medium text-[#0F172A]">
                          {r.item_name || '—'}
                        </td>
                        <td
                          className={`px-3 py-1.5 ${isUnassigned ? 'text-neutral-400 italic' : 'text-neutral-700'}`}
                          data-testid={`allocation-details-location-${order.id}-${idx}`}
                        >
                          {r.allocated_location || 'Unassigned'}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums font-semibold">
                          {r.allocated_qty || 0}
                        </td>
                        <td className="px-3 py-1.5 font-mono text-neutral-700">
                          {r.order_number || '—'}
                        </td>
                        <td
                          className="px-3 py-1.5 text-right tabular-nums text-neutral-700"
                          data-testid={`allocation-details-remaining-${order.id}-${idx}`}
                        >
                          {r.remaining_after == null ? (
                            <span className="text-neutral-400">—</span>
                          ) : (
                            r.remaining_after
                          )}
                        </td>
                        <td
                          className="px-3 py-1.5 text-right tabular-nums"
                          data-testid={`allocation-details-shortfall-${order.id}-${idx}`}
                        >
                          {isShort ? (
                            <span className="inline-flex items-center gap-0.5 text-red-700 font-bold">
                              <AlertTriangle className="w-3 h-3" /> {r.shortfall_qty}
                            </span>
                          ) : (
                            <span className="text-neutral-400">0</span>
                          )}
                        </td>
                        {canManualAllocate && (
                          <td
                            className="px-3 py-1.5 text-right"
                            data-testid={`allocation-details-action-${order.id}-${idx}`}
                          >
                            {isShort && r.order_line_id ? (
                              <button
                                type="button"
                                onClick={() => setFillRow(r)}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-[#0078D4] text-[#0078D4] hover:bg-[#0078D4] hover:text-white text-[11px] font-semibold transition"
                                data-testid={`allocation-details-fill-shortfall-btn-${order.id}-${idx}`}
                              >
                                <Plus className="w-3 h-3" />
                                Allocate shortfall
                              </button>
                            ) : (
                              <span className="text-neutral-300">—</span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
          {hasShortfall && (
            <div className="mt-2 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-md px-2 py-1.5 flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" />
              One or more lines could not be fully allocated — top up stock or re-run the planner after replenishment.
            </div>
          )}
        </div>
      )}
      {fillRow && (
        <ShortfallFillModal
          order={order}
          row={fillRow}
          authHeaders={authHeaders}
          onClose={() => setFillRow(null)}
          onDone={() => {
            setFillRow(null);
            setReloadKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
};
